/**
 * The platform's container is a LOCATION (spec 2026-09-27, restaurant → location
 * rename). This module is the canonical home of the class and interface (moved
 * here from `../auth/Restaurant` by the Phase 3 file move).
 *
 * Note: `Location` shadows the DOM global `Location` type in any browser file
 * that imports it — alias it (`import { Location as LocationRecord }`) there.
 */
import type { HouseStyleProfile } from "../ai/houseStyle";
import type { LocationRemoteApprovalConfig } from "./LocationRemoteApproval";
import type { LocationShiftAlertsConfig } from "./LocationShiftAlerts";
import type {
  MenuSyncPriceAdjustment,
  MenuSyncPriceAdjustments,
} from "../integrations/Integration";
import type { IGuestWifiSettings } from "../guest_codes/GuestCode";
import type { KitchenPacingSettings } from "../kitchen/prepTimeQuote";
import type { LocationPrintingSettings } from "../kitchen/PrinterSettings";
import type { LocationGiftCardSettingsBlock } from "../giftCards/LocationGiftCardSettings";
import type { LocationBookingSettingsBlock } from "../appointments/BookingSettings";
import { barSpiritListsBlockFromJSON, type LocationBarSpiritListsBlock } from "../bar/BarSpiritList";
import { barWineListBlockFromJSON, type LocationBarWineListBlock } from "../bar/BarWineList";
import type { DeliveryOrderAutopilotConfig } from "../integrations/Integration";
import type { IGuestOrderingSettings } from "../guest_ordering/GuestOrderingSettings";
import type { OptimizedImage } from "../images/OptimizedImage";
import { readRenamed } from "../utils/readRenamed";
import type { DeliveryProviderKey } from "../notifications/Notification";
import type { IPosConfig } from "../orders/PosConfig";
import { buildPosConfig } from "../orders/PosConfig";
import type { IPaymentRoutingSettings } from "../payments/neutral/routing/PaymentRouting";
import type { PaymentMode } from "../payments/neutral/routing/PaymentMode";
import type { IGuestCardCapture } from "../payment/guestCardCapture";
import type { IMerchantAccountRouting } from "../payment/merchantAccounts";
import type { LocationAlertConfig } from "./LocationAlerts"; // scoped-path-ok: module path
import type { OrderAlertRoutingConfig } from "./OrderAlertRouting"; // scoped-path-ok: module path
import type { LocationCashTipSettings } from "./cashTipSettings"; // scoped-path-ok: module path
import type { LocationCardHoldSettings } from "./cardHoldSettings"; // scoped-path-ok: module path
import { canonicalDayKey, normalizeOperatingHoursKeys } from "../auth/operatingHours";

export interface LocationAddress {
  readonly street: string;
  readonly street2?: string; // Apt, suite, unit, floor, etc.
  readonly city: string;
  readonly state: string;
  readonly zipCode: string;
  readonly country: string;
}

export interface LocationContact {
  readonly phone: string;
  readonly email: string;
  readonly website?: string;
  readonly additionalPhones?: readonly string[]; // Secondary phone numbers
}

/**
 * Restaurant-wide receipt presentation.
 *
 * The gateway has been able to draw a footer, a website line and a tip table
 * since those renderers were written, and nothing has ever supplied any of the
 * three — the goldens depict a receipt production does not print. This is the
 * missing input, held on the restaurant because all three are a property of the
 * BUSINESS, not of the printer the job happened to reach.
 */
export interface LocationReceiptPresentation {
  /** Footer lines printed centred beneath the totals, in order. */
  readonly footerLines?: readonly string[];
  /**
   * Whole-percent tip options offered on a GUEST CHECK only (never on a paid
   * receipt). Amounts are computed server-side on the discount-net subtotal —
   * tip is never calculated on tax.
   */
  readonly tipPercents?: readonly number[];
}

/**
 * Hours-mode discriminator. Mutually-exclusive enum (NOT a boolean) so a
 * future `'always_open'` (24/7) value can be added additively without a
 * Zod migration or consumer churn.
 *
 *   - `'fixed'`           — restaurant has a 7-day open/close schedule.
 *                           This is the legacy default; existing docs
 *                           without `hoursMode` are treated as `'fixed'`.
 *   - `'by_appointment'`  — restaurant doesn't have a fixed schedule
 *                           (contractors, B2B operations, on-call services).
 *                           `operatingHours` is still persisted with
 *                           defaults so a switch-back-to-fixed doesn't
 *                           lose data, but UI gates on this flag.
 */
export type LocationHoursMode = 'fixed' | 'by_appointment';

export const LOCATION_HOURS_MODES: readonly LocationHoursMode[] = [
  'fixed',
  'by_appointment',
] as const;

export interface DayHours {
  readonly open: string;
  readonly close: string;
  readonly closed: boolean;
}

export type WeekStartDay =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

// ========================================
// Fees & Charges Configuration
// ========================================

export type GratuityChargeType = "percentage" | "flatAmount";

export interface GratuityChargeConfig {
  readonly enabled: boolean;
  readonly type: GratuityChargeType;
  readonly rate?: number; // Decimal (e.g., 0.18 for 18%) — used when type = 'percentage'
  readonly amount?: number; // Flat dollar amount — used when type = 'flatAmount'
  readonly description: string; // Display label, e.g. "Auto Gratuity"
  readonly applyToPartySize?: number; // Auto-apply when party >= N (0 or undefined = always)
  readonly taxable?: boolean; // Whether tax should be applied to the gratuity (legacy)
  readonly taxRateIds?: readonly number[]; // Tax rate entity IDs to apply to the gratuity
}

export type ServiceChargeType = "percentage" | "flatAmount";

export interface ServiceChargeConfig {
  readonly enabled: boolean;
  readonly type: ServiceChargeType;
  readonly rate?: number; // Decimal (e.g., 0.18 for 18%) — used when type = 'percentage'
  readonly amount?: number; // Flat dollar amount — used when type = 'flatAmount'
  readonly description: string; // Display label, e.g. "Service Charge"
  readonly applyToPartySize?: number; // Auto-apply when party >= N (0 or undefined = always)
}

export type DeliveryChargeType = "flat" | "percentage" | "perDistance";

export interface DeliveryChargeConfig {
  readonly enabled: boolean;
  readonly type: DeliveryChargeType;
  readonly flatAmount?: number; // Flat fee — used when type = 'flat'
  readonly percentage?: number; // Decimal (e.g., 0.05 for 5%) — used when type = 'percentage'
  readonly perDistanceRate?: number; // Rate per mile/km — used when type = 'perDistance'
  readonly minimumCharge?: number; // Floor charge
  readonly maximumCharge?: number; // Cap charge
  readonly freeDeliveryThreshold?: number; // Free delivery when subtotal exceeds this
  readonly description: string;
}

export interface CustomFeeConfig {
  readonly id: string; // Unique identifier
  readonly name: string; // Display name
  readonly enabled: boolean;
  readonly type: "percentage" | "flatAmount";
  readonly rate?: number; // Decimal — used when type = 'percentage'
  readonly amount?: number; // Flat amount — used when type = 'flatAmount'
  readonly description?: string;
  readonly applyToOrderTypes?: readonly string[]; // Empty = all order types
}

/**
 * The guest-facing Online Service Fee — restaurant-owned, client-writable.
 * The server clamps it at checkout and never trusts it raw (common
 * `resolveOnlineServiceFeeConfig`). Absent fields = defaults.
 */
export interface OnlineServiceFeeConfig {
  readonly enabled: boolean; // default true
  readonly amountCents: number; // default 110; server clamps to whole cents 0..1000
  readonly label: string; // default "Online Service Fee"; 1..40 chars
  readonly taxable: boolean; // default true
  /** `ITaxRate.taxRateId`s taxing the fee. Absent = the location's default Percent rates. */
  readonly taxRateIds: readonly number[];
}

/**
 * LEGACY: the retired owner acknowledgment of OrderUp's per-order fee. Its only
 * writer, `POST …/online-service-fee/acknowledge`, was removed by the owner
 * decision of 2026-10-06 (option A — the fee is automatic). Nothing writes or
 * reads it; it stays server-only (firestore rules `serverOnlySettingsBlocks`)
 * so a stored value is inert.
 */
export interface OnlineServiceFeeEnrollment {
  readonly acknowledgedAt: string; // ISO-8601
  /** OrderUp's per-order price the owner agreed to, in cents. */
  readonly platformFeeCents: number;
  // NO acknowledgedByUid: this document is publicly readable. Who acknowledged
  // is recorded only in Supabase (billing_state.online_order_fee_locations).
}

export interface FeesAndChargesConfig {
  readonly onlineServiceFee?: Partial<OnlineServiceFeeConfig>;
  readonly gratuityCharge?: GratuityChargeConfig;
  readonly serviceCharge?: ServiceChargeConfig;
  readonly deliveryCharge?: DeliveryChargeConfig;
  readonly customFees?: readonly CustomFeeConfig[];
}

/**
 * Per-restaurant policy that bounds the merchant's exposure when payments
 * are captured offline (no network at the time of card read).
 *
 * The full system: when `enabled=true` and the POS detects the API is
 * unreachable, the encrypted card payload is persisted on-device and the
 * payment is accepted with `paid_pending` semantics. The
 * `PaymentRecoveryService` replays the auth when connectivity returns,
 * keyed by the same idempotency key the original capture would have used
 * so the server cannot double-charge.
 *
 * All four limits are AND-ed at the admission gate (`RiskPolicyService`).
 * Crossing any one of them rejects new offline captures and the merchant
 * is told to use cash or wait for network.
 *
 * **Security note:** changes to these fields are auditable — the API
 * route layer must emit a Firestore audit event on every mutation. This
 * lives on `RestaurantSettings` (alongside `feesAndCharges`) because it
 * is operational policy, not per-method configuration, and the POS reads
 * it through the same `RestaurantCubit` stream that drives every other
 * operational decision.
 */
export interface OfflinePaymentSettings {
  /** Master switch. When false, every offline capture is denied regardless of other limits. */
  readonly enabled: boolean;

  /** Per-transaction ceiling in minor currency units (cents). Default: 5000 ($50). */
  readonly maxAmountCents: number;

  /** Maximum number of intents that may sit in the queue awaiting replay. Default: 20. */
  readonly maxQueueDepth: number;

  /** Sum (cents) of amount + tipAmount across all queued intents. Default: 50000 ($500). */
  readonly maxTotalDeferredCents: number;

  /** Max age (hours) of the oldest queued intent before new captures are blocked. Default: 24. */
  readonly maxAgeHours: number;

  /**
   * LOCATION-WIDE ceiling (cents) on total deferred across ALL devices at this
   * restaurant. `undefined` = no location ceiling (per-device gates only).
   * Catches the multi-terminal case where each device is individually under
   * its cap but the location total is risky. Enforced POS-side by
   * `RiskPolicyService` against the cross-device `offline_queue_status`
   * aggregate. (POS twin: `maxLocationTotalDeferredCents`.)
   */
  readonly maxLocationTotalDeferredCents?: number;

  /**
   * LOCATION-WIDE ceiling on the COUNT of pending offline intents across all
   * devices. `undefined` = no location count cap. (POS twin:
   * `maxLocationPendingCount`.)
   */
  readonly maxLocationPendingCount?: number;

  /**
   * Cardholder disclosure shown at an offline capture before storing the card
   * for a possible merchant-initiated re-bill (recovery / dunning). This is the
   * LEGAL keystone: the POS only presents the consent dialog — and the server
   * only opens a recovery campaign — once real wording is configured here.
   * Empty/undefined ⇒ the consent flow is unarmed and recovery stays dark
   * end-to-end (no placeholder wording is ever shown as if blessed). Set by the
   * merchant's risk/legal owner in the management app. (POS twin:
   * `consentDisclosureText`.)
   */
  readonly consentDisclosureText?: string;

  /**
   * Version/identifier of `consentDisclosureText` (e.g. `"1.0"`). Pins WHICH
   * wording a cardholder agreed to — dispute evidence stamped on every recovered
   * PaymentRecord. Bump it whenever the text changes. Empty/undefined ⇒ unarmed.
   * (POS twin: `consentDisclosureVersion`.)
   */
  readonly consentDisclosureVersion?: string;
}

/**
 * Canonical PLATFORM DEFAULT offline-payments policy — applied when a
 * restaurant has not configured one, and the seed value when the management UI
 * first renders the form.
 *
 * `enabled` was `false` here, which made offline card acceptance a silent
 * ABSENCE rather than a decision. Measured against production on 2026-08-28,
 * two of three live restaurants had no `offlinePayments` field at all, and the
 * effect was not a conservative fallback: with the policy disabled the POS path
 * cubit stays on the direct tier even when the API is unreachable, so during an
 * outage every card tap simply FAILED and nothing was queued. The whole
 * T2/T3/T4 ladder was inert for want of a field nobody set.
 *
 * The caps are unchanged and deliberately conservative. Consent wording stays
 * absent, so `consentArmed` is false, the POS presents no disclosure dialog and
 * the server opens no recovery campaign — capture works, dunning does not start
 * itself. A merchant can still opt out explicitly with `enabled: false`.
 *
 * Mirrors the POS-side
 * `apps/pos/lib/models/payments/offline_payment_settings.dart`'s
 * `platformDefault` static const so the defaults stay consistent across the
 * stack. Update this and the POS-side const together — drift is exactly the bug
 * class an "industry level" architecture has to preclude.
 */
export const DEFAULT_OFFLINE_PAYMENT_SETTINGS: OfflinePaymentSettings = {
  enabled: true,
  maxAmountCents: 5000, // $50
  maxQueueDepth: 20,
  maxTotalDeferredCents: 50000, // $500
  maxAgeHours: 24,
};

/**
 * Canonical bounds for the offline-payments policy fields. Single
 * source of truth shared by:
 *   * The Zod schema (`OfflinePaymentSettingsSchema.max(...)`)
 *     in `packages/schemas/src/auth/Location.ts`.
 *   * The management UI's input controls (`<input max={...}>`)
 *     in `apps/management/src/app/settings/restaurant-settings/
 *     offline-payments/page.tsx`.
 *
 * The numeric ceilings are deliberately tight — bigger amounts /
 * deeper queues / longer ages exit the regime we sized the recovery
 * loop and risk-policy service for. Loosening any of these requires
 * a corresponding update to those services AND to the per-restaurant
 * fraud-exposure analysis the limits embody.
 */
export const OFFLINE_PAYMENT_SETTINGS_BOUNDS = {
  /** Per-capture ceiling in cents — $1,000.00. */
  maxAmountCentsCeiling: 100000,
  /** Total deferred ceiling in cents — $10,000.00. */
  maxTotalDeferredCentsCeiling: 1000000,
  /** Maximum number of intents the queue may hold. */
  maxQueueDepthCeiling: 100,
  /** Maximum age (hours) — 72h matches the TRX processor window. */
  maxAgeHoursCeiling: 72,
  /**
   * LOCATION-WIDE total deferred ceiling (cents) — $50,000.00. Higher than the
   * per-device ceiling since it sums across all terminals at a restaurant.
   */
  maxLocationTotalDeferredCentsCeiling: 5000000,
  /** LOCATION-WIDE pending-intent count ceiling across all terminals. */
  maxLocationPendingCountCeiling: 500,
} as const;

export interface LocationFeatures {
  /**
   * Automatic review requests — asking a guest for a Google review after their
   * order.
   *
   * OPTIONAL and absent-means-OFF, because this sends MARKETING EMAIL under the
   * restaurant's name. A global env flag alone is not consent: it says the
   * platform is willing to send, not that this restaurant asked us to. Both
   * must be true before a single ask goes out.
   */
  readonly reviewRequests?: boolean;
  /**
   * Text alerts — the location may send SMS (manager alerts from the POS,
   * guest order texts, courier updates, reminders). LOCKED by default
   * (owner, 2026-09-26): texting is an add-on, not a default.
   *
   * This flag is the STAFF switch, set by an OrderUp admin in the admin
   * Features tab. `firestore.rules` refuses any change to it from anyone but a
   * site admin, so an owner cannot unlock their own location. The paid path is
   * a `text_alerts` add-on in `billing_state`, which the send-time check reads
   * directly. Enforced for every text in `BaseSmsService`
   * (`services/sms/textAlertsAccess.ts`); absent means locked.
   */
  readonly textAlerts?: boolean;
  readonly website: boolean;
  readonly giftCards: boolean;
  readonly advertising: boolean;
  readonly orderUpBooks: boolean;
  readonly payroll: boolean;
  readonly posEnabled: boolean;
  /**
   * RecipeOps inventory + recipe costing add-on (Phase 14). When true the POS
   * order-close path triggers `book_decrement_inventory_for_order` to decrement
   * inventory lots and post a COGS journal entry. Defaults to false to keep
   * legacy restaurants opt-in.
   */
  readonly recipeOps: boolean;
  /**
   * Firm Portal — the cross-tenant "Clients" workspace where an accounting
   * firm opens its clients' books via consented accountant/manager grants.
   * Surfaced in the admin Features tab as "Client Management" (the key stays
   * `firmPortal` to avoid colliding with the customer CRM at `/books/customers`
   * and to match the existing `useFirmClients` code). Decoupled from
   * `dashboardLayout === "financial"` so it can be enabled for any business and
   * disabled for a financial one. Defaults false; `defaultFeaturesForBusinessType`
   * seeds it true for the financial layout.
   */
  readonly firmPortal: boolean;
  /**
   * Location Drive paid-storage add-on (OrderUp Drive). OPTIONAL and distinct
   * from the other flags: the location drive itself is ALWAYS available at the
   * free 25 GB tier (the `locationDrive` management feature is `always`
   * entitled), so this flag does NOT gate access. It is set true by
   * `RestaurantFeatureFlagSyncService` only when the location holds an active
   * paid `location_drive` add-on, signalling "this location has a paid storage
   * plan". The actual quota ceiling lives on `fs.namespaces.quota_bytes`,
   * re-stamped on tier change — this flag is just the entitlement mirror.
   */
  readonly locationDrive?: boolean;
  /**
   * Appointments/booking vertical (Phase C). When true the business exposes the
   * Appointments management surface (service catalog, provider availability,
   * calendar) and the public booking flow. OPTIONAL, defaults false so existing
   * restaurants are unaffected; `defaultFeaturesForBusinessType` seeds it true
   * for the `appointments` dashboard layout (beauty/real-estate/etc.).
   */
  readonly appointments?: boolean;
  /**
   * OrderUp Studio — the freeform design canvas (flyers, posters, table tents,
   * social posts). OPTIONAL and OPT-IN-OFF: absent means LOCKED, so every
   * restaurant document that predates this flag stays dark. Nothing sets it
   * today, which is the point — the studio ships behind this gate and an
   * OrderUp admin enables it per location from the Features tab.
   *
   * Consumers MUST pass `defaultEnabled={false}` to `FeatureGatePage`. That
   * component defaults to `true` for the legacy opt-in-ON flags, so relying on
   * the default here would open the gate on every legacy doc — the same trap
   * `firmPortal` and `recipeOps` already document.
   */
  readonly designStudio?: boolean;
  /**
   * Property Management vertical (PM-0). When true the business exposes the
   * Property Management surface — properties, buildings, units and unit areas
   * (`pm_properties` / `pm_buildings` / `pm_units` / `pm_unit_areas`, all in
   * Supabase `public`, reached only through apps/api under the service role).
   *
   * OPTIONAL, defaults false. Unlike `appointments` it is NOT seeded by any
   * `dashboardLayout`: the `appointments` layout is shared by beauty, health,
   * fitness and pet verticals, none of which manage property, so seeding off
   * the layout would switch the section on for a nail salon. It is an explicit
   * per-location decision made in the admin Features tab.
   *
   * NOTE ON THE WORD "tenant": every `tenant_guid` on those tables is the
   * MANAGEMENT COMPANY, the same sense it carries everywhere else in OrderUp.
   * The person renting a unit is a RESIDENT, in every identifier and comment.
   */
  readonly propertyManagement?: boolean;
  /**
   * Remote manager approval — a manager who is away approves a till's
   * manager-PIN action from OrderUp Manager or a one-time text link (spec
   * docs/superpowers/specs/2026-10-16-remote-manager-approval-design.md).
   * A PAID add-on (owner, 2026-10-17): OPTIONAL and absent-means-LOCKED.
   *
   * Written only by the entitlement projector (billing feature key
   * `remote_manager_approval`) or an OrderUp admin; `firestore.rules` refuses
   * any other client change to it. `ManagerApprovalService` reads it on every
   * config read, create, decision and redeem, so a location without the add-on
   * shows no "Ask a manager" button and cannot spend an approval. Its texts
   * still need `textAlerts` as well.
   */
  readonly remoteManagerApproval?: boolean;
  /**
   * Scheduling. Basic is free for every location, so this defaults ON and
   * absent reads ON; the paid Pro tier is enforced by the scheduling
   * capabilities (weather, horizon), not by this flag. Written only by the
   * entitlement projector or an OrderUp admin (`firestore.rules`).
   */
  readonly scheduling?: boolean;
  /**
   * Customer loyalty — points, rewards, enrolment and card-linked recognition
   * for this location's guests (docs/customer-loyalty/, docs/CARD_LINKED_LOYALTY.md).
   * A per-location SUBSCRIPTION TOGGLE (owner, 2026-11-01): OPTIONAL and
   * absent-means-LOCKED, so every location that predates it stays dark.
   *
   * Written only by the entitlement projector (billing feature key
   * `customer_loyalty`) or an OrderUp admin; `firestore.rules` refuses any other
   * client change to it. Read with the global kill switch
   * `initiative.customer_loyalty` by `resolveCustomerLoyaltyAccess`
   * (packages/core) — loyalty is live at a location only when BOTH are on.
   */
  readonly customerLoyalty?: boolean;
}

/**
 * The default for every feature flag, and the ONLY list feature hydration walks.
 *
 * ⚠️ TYPED `Record<keyof RestaurantFeatures, boolean>` ON PURPOSE. A flag added
 * to the interface with no entry here is a COMPILE ERROR — not a field that
 * silently vanishes from the document.
 *
 * The constructor used to hydrate `features` by naming each key inline, which
 * meant a new flag had to be remembered in a place nothing pointed at. It was
 * missed three times (`locationDrive`, `appointments`, `propertyManagement`),
 * each fixed by adding a key and a comment asking the next person to remember.
 * `designStudio` was missed a fourth time and shipped to production: the admin
 * Features toggle wrote it, the constructor dropped it on the next read, the
 * switch sprang back to off, and `toJSON` then wrote the stripped map back —
 * so an unrelated edit destroyed the flag in Firestore. A fourth comment would
 * not have stopped a fifth, so the reminder is now a type.
 *
 * This is the same defect `RestaurantFeaturesSchema` documents one layer up:
 * `features` is written to Firestore as a WHOLE MAP, so any list that rebuilds
 * it must be exhaustive or it deletes what it does not name.
 *
 * ⚠️ ONE exhaustive record lives OUTSIDE this package and a packages-only
 * type-check will not compile it: `FEATURE_NAV_SECTIONS` in
 * `apps/management/src/components/app-sidebar.tsx`. It is typed the same way,
 * so it DOES fail the build — just not the one you are likely to run after
 * editing this interface. `reviewRequests` (2026-09-14) landed green in
 * packages/ and left apps/management red. After adding a key here, type-check
 * apps/management too.
 */
export const LOCATION_FEATURE_DEFAULTS: Record<keyof LocationFeatures, boolean> = {
  website: true,
  giftCards: true,
  advertising: true,
  orderUpBooks: true,
  payroll: true,
  posEnabled: true,
  recipeOps: false,
  firmPortal: false,
  locationDrive: false,
  appointments: false,
  designStudio: false,
  propertyManagement: false,
  // Sends marketing email under the restaurant's name, so it starts OFF and
  // stays off until the restaurant asks for it.
  reviewRequests: false,
  // Texting is an add-on: locked until OrderUp or a paid add-on unlocks it.
  textAlerts: false,
  // A paid add-on: locked until OrderUp or the owner's entitlement unlocks it.
  remoteManagerApproval: false,
  // Scheduling Basic is free for everyone.
  scheduling: true,
  // A subscription toggle: locked until OrderUp switches it on for the location.
  customerLoyalty: false,
};

/**
 * A location's feature flags as a DECISION reads them: every flag at its stored
 * value, else its default, and a document with no `features` map at all reads
 * every default — a location the entitlement projector has not reached yet runs
 * on them (`billing/newLocationFeatureSetup.ts`).
 *
 * This is how the web reads the same document: `hydrateLocationFeatures` fills
 * each absent flag, and its navigation reads an absent map as POS on with every
 * default-on section open (`apps/management` `app-sidebar.tsx`). The management
 * door and the phones' access answer read the flags through THIS, so a flag the
 * stored map omits is never on for one of them and off for another.
 *
 * Not the paid-feature enforcement's reading: `platformFeatureAccess.ts` refuses
 * an absent flag on purpose, so a location that was never projected is not
 * allowed a paid write.
 */
export const resolveLocationFeatures = (
  stored: Partial<LocationFeatures> | null | undefined,
): LocationFeatures => {
  const keys = Object.keys(LOCATION_FEATURE_DEFAULTS) as (keyof LocationFeatures)[];
  return Object.fromEntries(
    keys.map((key) => [key, stored?.[key] ?? LOCATION_FEATURE_DEFAULTS[key]]),
  ) as unknown as LocationFeatures;
};

/**
 * Hydrate a stored `features` map, filling every absent flag with its default.
 *
 * Returns `undefined` for an absent map so a document that has never had
 * features stays distinguishable from one with all flags at their defaults —
 * the constructor has always drawn that line and callers depend on it. A
 * decision that needs the flags themselves reads `resolveLocationFeatures`.
 */
export const hydrateLocationFeatures = (
  stored: Partial<LocationFeatures> | undefined,
): LocationFeatures | undefined => (stored ? resolveLocationFeatures(stored) : undefined);

export type ClockFormat = "12h" | "24h";
export type DateFormatPreset =
  | "MM-DD-YYYY"
  | "DD-MM-YYYY"
  | "YYYY-MM-DD"
  | "MM/DD/YYYY"
  | "DD/MM/YYYY";

/**
 * Scheduling / labor-compliance configuration. Thresholds drive the
 * ClockInMonitor (late detection) and the ScheduleService labor-hour
 * guardrail. Absent = default (15 min grace, no weekly cap).
 */
export interface SchedulingConfig {
  /** Minutes past shift start before a clock-in is flagged as late. Default 15. */
  readonly lateThresholdMinutes?: number;
  /** Weekly-hour ceiling per employee. Shift assignment is blocked beyond this when scheduling.laborGuardrails is entitled. */
  readonly maxWeeklyHoursPerEmployee?: number;
}

/**
 * Cash-tip loss-prevention policy (Stage 4), per-restaurant. When `enabled`, a
 * cash check closed with a declared tip BELOW `thresholdPercent` of the check
 * opens a manager review (soft gate: the check still closes; hard gate: a
 * manager PIN is required to close). Escalation SLAs (seconds) drive the durable
 * cron sweep; `escalationPhones` are the SMS fallback recipients.
 */
export interface CashTipGovernanceSettings {
  readonly enabled: boolean;
  /** Hard gate (manager PIN to close) vs soft gate (close + review). Default soft. */
  readonly hardGate?: boolean;
  /** Flag cash tips below this % of the check. Default 15. */
  readonly thresholdPercent?: number;
  /** Skip checks smaller than this (cents) to avoid flagging tiny tickets. Default 0. */
  readonly minCheckAmountCents?: number;
  /** Escalation tier SLAs (seconds from open). Defaults 900 / 2700 / 7200. */
  readonly escalationTier1Seconds?: number;
  readonly escalationTier2Seconds?: number;
  readonly escalationTier3Seconds?: number;
  /** Terminal auto-close SLA (seconds). Default 14400 (4h). */
  readonly autoCloseSeconds?: number;
  /** SMS fallback recipients for tier-2+ escalation when no on-duty manager resolves. */
  readonly escalationPhones?: readonly string[];
}

/**
 * How the POS collects card tips — restaurant-level policy driving the
 * card payment flow. Gated on `allowTips` (when tips are off entirely,
 * the mode is ignored and behaves as `'none'`).
 *
 *   - `'none'`          → no tip prompt; charge the exact amount.
 *   - `'beforePayment'` → guest picks tip BEFORE the card is read; one
 *                         one-step sale for base + tip (counter service).
 *   - `'afterAuth'`     → two-step, tip taken AT THE TERMINAL: authorize,
 *                         enter the tip on screen, capture base + tip in the
 *                         same flow. Nothing is left open when the guest goes.
 *   - `'onReceipt'`     → two-step, tip taken FROM THE PRINTED SLIP: authorize
 *                         the check amount, close the check, print blank Tip /
 *                         Total lines. Staff key the written tip in later from
 *                         POS Tip Adjust, which captures base + tip against the
 *                         same hold; anything un-adjusted is captured at base by
 *                         the pre-batch-close sweep.
 *
 * `'afterAuth'` and `'onReceipt'` differ only in WHEN the tip is entered — the
 * card leg is the same authorization either way. Only `'onReceipt'` leaves a
 * payment `awaitingCapture` after the guest leaves, which is the sole state a
 * tip-adjust can act on.
 *
 * BOTH need a provider that can hold an authorization. On a provider that
 * cannot they
 * degrade to `'beforePayment'`, never to each other: offering a later
 * tip-adjust on a rail with no open hold is a button that always answers "no
 * adjustable payment".
 */
export type CardTipCollectionMode =
  | "none"
  | "beforePayment"
  | "afterAuth"
  | "onReceipt";

/**
 * Which base the tip-percentage presets apply to on the tip-capture dialog.
 *
 *   - `'discountedSubtotal'` → tip % × (subtotal − discounts). Default — you
 *                              don't tip on tax or auto-gratuity.
 *   - `'subtotal'`           → tip % × subtotal (ignores applied discounts).
 *   - `'total'`              → tip % × payment amount (legacy post-tax behavior).
 */
export type TipBasis = "discountedSubtotal" | "subtotal" | "total";

/**
 * Unified order-acceptance policy — applies to EVERY order source (first-party
 * online ordering AND third-party marketplace) via the shared acceptance spine.
 * See docs/UNIFIED_ORDER_ACCEPTANCE_ARCHITECTURE.md.
 *  - `auto`   — validate stock, then accept and fire to the kitchen (at the
 *               prep lead time for scheduled orders).
 *  - `manual` — hold in `pendingAcceptance`; staff Accept on the POS before the
 *               kitchen fires (a future order is then held until its fire time).
 * ONE setting for every incoming source (`resolveAcceptancePolicy`, spec B0;
 * owner decision 2026-09-25), edited in the management app only; unset
 * resolves to `manual`. It replaced the legacy `autoAcceptOrders` boolean,
 * which old documents may still carry and nothing reads
 * (`scripts/ops/backfill-order-acceptance-mode.ts` moved it onto this field).
 */
export type OrderAcceptanceMode = "auto" | "manual";

/**
 * `settings.*` keys that each have ONE dedicated editor and are written only by
 * dotted path (`settings.<key>`). A writer that submits a whole `settings` map
 * (the site-admin RestaurantModal round-trips the hydrated map) never touches
 * them: the client service strips them from the map before writing, so a map
 * hydrated before another screen's save can neither revert nor delete them.
 *
 * - `orderAcceptanceMode` — management's order-acceptance page.
 * - `printing` — management's Ticket Appearance modal / Print coordinator card.
 * - `marketplaceOrderEmployeeId` — management's POS settings page (Delivery-app
 *   orders card).
 * - `missedPunchAlerts` — management's Text Alerts page, by dotted path (a manager
 *   may write it from the browser; it is NOT server-only).
 * - `giftCards`, `booking`, `barSpiritLists`, `barWineList`, `cashTips`, `cardHolds`, `onlineServiceFeeEnrollment`, `shiftAlerts`, `allowStaffAppClockIn` — the API (Admin SDK) only; no
 *   client may write them (`serverOnlySettingsBlocks` in the rules refuses it).
 *   `shiftAlerts`' one writer is `PUT /shift-alerts/config`;
 *   `allowStaffAppClockIn`'s is `PUT /time-clock/settings`.
 *
 * Phase 2 of the settings migration adds each new block here (e.g.
 * `giftCards`, `booking`) in the same change that moves it.
 */
export const LOCATION_SETTINGS_KEYS_WITH_DEDICATED_WRITER = [
  "orderAcceptanceMode",
  "printing",
  "marketplaceOrderEmployeeId",
  "giftCards",
  "booking",
  "barSpiritLists",
  "barWineList",
  "cashTips",
  "cardHolds",
  "onlineServiceFeeEnrollment",
  "shiftAlerts",
  "allowStaffAppClockIn",
  "missedPunchAlerts",
] as const satisfies ReadonlyArray<keyof LocationSettings>;

export interface LocationSettings {
  readonly currency: string; // ISO 4217 currency code (e.g., USD, EUR, GBP)
  readonly allowTips: boolean; // Whether tips are accepted
  /**
   * Card tip collection policy — see {@link CardTipCollectionMode}. Unset reads
   * as `'beforePayment'`: an on-screen tip works on every provider and leaves
   * no uncaptured hold, so it is the safe default for a restaurant that has not
   * chosen. Full-service venues opt into `'onReceipt'` (tip written on the
   * printed slip, keyed in later) or `'afterAuth'` (tip entered at the
   * terminal).
   */
  readonly cardTipCollectionMode?: CardTipCollectionMode;
  /**
   * Tip auth buffer (owner decision 2026-10-04, docs/CARD_TIPPING_ARCHITECTURE.md
   * §5b): a card hold on a TWO-STEP rail (`afterAuth` / `onReceipt`) authorizes
   * base + 25% (`tipAuthBufferMinor`, `@order-up/common`) so tip presets up to
   * 25% fit inside the hold and are never flagged. Unset reads as ON
   * (`isTipAuthBufferSettingOn`); a location manager turns it off with `false`.
   * Effective only while the tenant's `initiative.tip_auth_buffer` flag is on.
   * Never applies to a one-step sale or an online hold.
   */
  readonly tipAuthBuffer?: boolean;
  /**
   * Which base the tip-percentage presets apply to. See {@link TipBasis}.
   * Default `'discountedSubtotal'` — tip percentages apply to subtotal minus
   * discounts, excluding tax and auto-gratuity.
   */
  readonly tipBasis?: TipBasis;
  /**
   * The acceptance mode for EVERY incoming source — online and every
   * marketplace. The ONE acceptance setting: edited in the management app
   * only, with no per-marketplace override. Unset resolves to `'manual'`.
   * See docs/UNIFIED_ORDER_ACCEPTANCE_ARCHITECTURE.md §4.
   */
  readonly orderAcceptanceMode?: OrderAcceptanceMode;
  readonly guestWifi?: IGuestWifiSettings; // Guest-facing wifi printed on QR codes (NOT NetworkConfig)
  /**
   * Guest table page, service requests, and (later pieces) pay-at-table and
   * tabs. Every reader resolves this through
   * `resolveGuestOrderingSettings(restaurant.settings?.guestOrdering)` rather
   * than reading fields off this object directly — see its JSDoc.
   */
  readonly guestOrdering?: IGuestOrderingSettings;
  readonly weekStartDay: WeekStartDay; // Which day the business week starts on
  /**
   * IANA timezone for this location (e.g. 'America/New_York') — the business
   * timezone schedule times are DISPLAYED and INTERPRETED in. When unset, the
   * scheduling layer falls back to the tenant timezone, then 'UTC'. Stored
   * per-restaurant so a brand with locations in different zones can differ.
   */
  readonly timezone?: string;
  readonly clockFormat?: ClockFormat; // 12-hour (AM/PM) or 24-hour clock display
  readonly dateFormat?: DateFormatPreset; // Date display format preference
  readonly defaultLanguage?: string; // Default POS UI language (BCP-47, e.g. 'en', 'es'); null = device locale
  readonly showSeconds?: boolean; // Show seconds in time displays
  readonly alerts?: LocationAlertConfig; // SMS alert configuration
  readonly orderAlertRouting?: OrderAlertRoutingConfig; // New-order awareness routing (per-channel target/ack/tiers)
  /** Remote manager approval — who may approve from OrderUp Manager or a text link. Read it through `readRemoteApprovalConfig`. */
  readonly remoteApproval?: LocationRemoteApprovalConfig;
  /** Shift reminders, lateness alerts and the on-time record; absent = off. Read it through `readShiftAlertsConfig`. */
  readonly shiftAlerts?: LocationShiftAlertsConfig;
  /**
   * Whether managers are alerted when someone has been clocked in for 16 hours
   * or more. Absent = off. Written from management's Text Alerts page by dotted
   * path (`settings.missedPunchAlerts`); read strictly as `enabled === true`.
   */
  readonly missedPunchAlerts?: { readonly enabled: boolean };
  readonly takeoutGuestCount?: 0 | 1; // Default guest count for non-dine-in orders (0 = no guests, 1 = count as 1)
  /**
   * The employee that OWNS every delivery-app order (Uber Eats, DoorDash,
   * Grubhub and every marketplace KitchenHub brokers) — normally the shared
   * Takeout STATION account (`isStationAccount`). Stamped as the order's
   * `assignedEmployeeId` when the order is created
   * (`marketplaceOrderOwnerFields`), so the order shows under that sign-in in
   * the POS Order Hub and on every "whose ticket is this" surface.
   *
   * Unset = the order has no owner (the behaviour before this setting). It is
   * read at CREATE time only: changing it never re-owns an existing order.
   * Configured in Management → Settings → POS.
   */
  readonly marketplaceOrderEmployeeId?: number;
  /**
   * Daily batch-out cutoff in HH:mm (24h), e.g. "21:00". Interpreted in the
   * TENANT's `settings.timezone`. Drives TWO things:
   *   1. the business-day boundary for daily order-number sequences
   *      (`counterService.getBusinessDateKey`, mirrored in POS
   *      `business_date_utils.dart`), and
   *   2. the card batch close — the API's batch-auto-close sweep closes the
   *      open TRX batch once this cutoff passes locally, and
   *   3. the cash-drawer end-of-day — the same sweep force-closes any shift
   *      still live on an earlier business date.
   * (2) applies to TRX (a processor that closes batches host-side would
   * not be moved by this setting). (3) applies to every restaurant, including
   * cash-only ones that have no card processor at all.
   */
  readonly batchOutTime?: string;
  readonly autoClockOutTime?: string; // Daily auto-clock-out time in HH:mm format (24h), e.g. "02:00"
  readonly lastAutoClockOutDate?: string; // "YYYY-MM-DD" guard against re-processing (set by cloud function)
  /** "YYYY-MM-DD" (tenant-local) guard so the batch-auto-close sweep fires once per business day. Server-written. */
  readonly lastAutoBatchCloseDate?: string;
  /**
   * "YYYY-MM-DD" (tenant-local) guard so the cash-drawer end-of-day sweep runs
   * once per business day. Server-written.
   *
   * Deliberately SEPARATE from {@link lastAutoBatchCloseDate}. The two closes
   * share a cutoff but not a fate: a card batch the gateway declines leaves its
   * own marker unstamped so the next tick retries, and a single shared marker
   * would then re-run the drawer sweep every five minutes for the rest of the
   * day. More importantly, a cash-only restaurant has no `providerRouting` and
   * is skipped by the card pass entirely — it still has tills to close.
   */
  readonly lastAutoDrawerCloseDate?: string;
  readonly feesAndCharges?: FeesAndChargesConfig; // Service charges, delivery fees, custom fees
  /** Offline payment policy — opt-in per restaurant. See `OfflinePaymentSettings`. */
  readonly offlinePayments?: OfflinePaymentSettings;
  readonly scheduling?: SchedulingConfig; // Scheduling / labor-compliance thresholds
  /**
   * Total guest-facing seat count for the dining room. Drives the
   * RevPASH (Revenue Per Available Seat-Hour) calculation on the Books
   * KPI dashboard. Optional — when missing the dashboard reports the KPI
   * with `status: 'unset'` and a note. Phase 16-C.
   */
  readonly seatCount?: number;
  /**
   * Whether the POS shows the per-seat numbering UI (seat-table diagram
   * and grouped sections on the order ticket). Defaults to `true`. When
   * `false` the order builder hides the seat diagram, and the per-seat
   * section grouping flattens to a single shared list — but item-level
   * `seatNumber` data is preserved, so toggling back on restores the
   * groupings without loss.
   */
  readonly enableSeatNumbering?: boolean;
  /**
   * Whether the POS captures a photo at clock-in and clock-out for manager
   * audit (anti-buddy-punching). Default off — this is opt-in per restaurant.
   *
   * The photo is a PLAIN image stored under a private prefix
   * (`timeclock-photos/{tenantDocId}/{restaurantDocId}/…`) and reviewed by
   * managers via a server-minted signed URL. It is NOT run through face
   * recognition and no biometric template is derived from it — deliberately, so
   * it does not create a "biometric identifier" under BIPA and similar laws.
   * Capture is best-effort: if the device has no camera or capture fails, the
   * punch still succeeds (offline-first + optional hardware).
   */
  readonly requireTimeClockPhoto?: boolean;
  /**
   * Whether staff may clock in and out from the staff app at this location
   * (spec §14b, D20). Absent or `true` = allowed, which is how every location
   * behaved before the switch existed; only an explicit `false` turns it off.
   * Read it through `isStaffAppClockInAllowed` (`./staffAppClockIn`), never by hand.
   *
   * When it is `false` the API refuses the staff app's punch-in AND punch-out
   * (403 `TIME_CLOCK_STAFF_APP_DISABLED`), so every punch goes through the
   * till, which writes its time entries directly and is unaffected — the way a
   * location that requires clock-in photos stops a photo-less phone punch.
   *
   * SERVER-WRITTEN: the rules refuse every client write
   * (`serverOnlySettingsBlocks`); the one writer is `PUT /time-clock/settings`
   * (owner, admin or a manager of this location), which audits each change.
   */
  readonly allowStaffAppClockIn?: boolean;
  /**
   * Whether sending a QUICK order (one not started from a table) prompts for
   * the dining option first and then, for takeout or delivery, for the
   * guest's phone and name — so the chit the expediter bags from carries who
   * the food is for. Default off (opt-in per restaurant).
   *
   * Only the FIRST send prompts; an order started from a table is already
   * dine-in and never does. Every step is skippable: a prompt that can block
   * a send during a rush is one staff learn to route around.
   */
  readonly promptTakeoutCustomerInfo?: boolean;
  /**
   * Whether staff may SKIP the takeout customer prompt
   * ([promptTakeoutCustomerInfo]). Absent = true (the prompt's original
   * behaviour). When false the POS offers no Skip and will not send the order
   * until a phone number and a name are entered — the owner's choice
   * (2026-09-25), for a restaurant that wants every takeout bag named.
   */
  readonly takeoutIntakeAllowSkip?: boolean;
  /**
   * Last-mile courier dispatch provider used by the delivery-dispatch
   * spine (`DeliveryDispatchService`). When a `'delivery'`-type order is
   * accepted, the dispatch service consults this field to decide which
   * provider's courier adapter (DoorDash Drive today; future Grubhub
   * `'drive'`-shaped APIs) to request a delivery from.
   *
   *   - `'none'` / `undefined` → no automatic Drive dispatch. The
   *      restaurant either handles last-mile in-house OR uses a
   *      marketplace platform whose courier is BYOC-shaped (Uber Eats —
   *      they own the courier, we just push GPS up).
   *   - `'doordash'` → DoorDash Drive (capability probe: courier adapter
   *      implements `createDelivery`). Stage 2 default reference impl.
   *   - `'ubereats'` / `'grubhub'` → reserved; the capability probe in
   *      the dispatch service skips providers without `createDelivery`
   *      (returns `'provider-not-applicable'`) so this is safe to set
   *      ahead of a provider gaining the dispatch surface.
   *
   * Additive, non-breaking — existing restaurants without this field
   * are treated as `'none'`. Provider-neutral by design: the dispatch
   * service resolves the courier via the delivery registry keyed on
   * this value (no `switch(provider)` outside the provider folders).
   */
  /**
   * Which ADAPTER dispatches couriers for this restaurant, or "none".
   *
   * An adapter key, not a marketplace: it is used to look up the Integration by
   * type and to resolve the courier module from the delivery registry. It was
   * typed `DeliveryPlatform` while those were the same union; KitchenHub split
   * them (docs/KITCHENHUB_API.md D1) and this side is unambiguously the adapter.
   */
  readonly courierProvider?: DeliveryProviderKey | "none";
  /**
   * Whether OrderUp sends the customer courier-milestone SMS (confirmed /
   * picked-up / delivered) for this restaurant's Drive deliveries.
   *
   * Opt-in (defaults to `false`/undefined) for TCPA/consent safety — the
   * restaurant must explicitly enable it. When `true`, the non-order
   * orchestrator's `delivery.status` handler sends a one-shot SMS per canonical
   * milestone via `ISmsService` (deduped on `Order.notifiedCourierMilestones`),
   * and dispatch turns OFF DoorDash's own `dropoff_contact_send_notifications`
   * so the customer is not double-texted. Suppressed entirely for `isTest`
   * orders regardless of this flag.
   */
  readonly customerCourierSmsEnabled?: boolean;
  /**
   * Courier dropoff policy — operator-set, applied to every Drive delivery
   * this restaurant dispatches. These are POLICY, not per-order data: there is
   * no customer-facing checkout control for them, so the restaurant decides
   * once and the dispatch spine applies it uniformly.
   *
   * `courierContactlessDropoff` and `courierDropoffSignatureRequired` are
   * MUTUALLY EXCLUSIVE on the DoorDash side (a signature cannot be collected
   * contactlessly). When both are set, contactless wins and the signature is
   * dropped — dispatch never sends the conflicting pair, which the provider
   * would reject outright.
   *
   * All default to `undefined` (feature absent on the wire), never `false` —
   * an unset option and an explicitly-disabled one are different to Drive.
   */
  readonly courierContactlessDropoff?: boolean;
  /** Drive `dropoff_options.signature`. See {@link courierContactlessDropoff}. */
  readonly courierDropoffSignatureRequired?: boolean;
  /** Drive `dropoff_options.id_verification` — checks the recipient's ID. */
  readonly courierIdVerificationRequired?: boolean;
  /** Drive `dropoff_options.proof_of_delivery` — Dasher photographs the dropoff. */
  readonly courierDropoffPhotoRequired?: boolean;
  /**
   * STANDARD PREP TIME, in minutes — how long an ordinary order takes when the
   * kitchen is not busy. The ONE restaurant-wide base for every order source
   * (in-house, online, marketplace), read only through
   * `resolvePrepLeadMinutes` (20 when unset). Every quote starts here and adds
   * for the live kitchen (`kitchenPacing`); the scheduled-order fire lead and
   * the courier pickup time come from the same quote.
   */
  readonly estimatedPrepTimeMinutes?: number;
  /**
   * THE fulfilment autopilot's switch and windows for EVERY order this
   * restaurant takes — online, till takeout and marketplace alike
   * (docs/KITCHENHUB_ORDER_AUTOPILOT.md §11). ABSENT MEANS ON with the default
   * windows; only `enabled: false` switches it off. A marketplace integration
   * may override a field for its own orders (`config.orderAutopilot`, same
   * shape), but cannot switch it back on.
   */
  readonly orderAutopilot?: DeliveryOrderAutopilotConfig;
  /**
   * Dynamic prep time — ALWAYS on: the load tiers, large-order bump, cap and
   * "busy right now" applied on top of the standard prep time for every order.
   * Absent fields take the defaults. SSOT: docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md;
   * resolved by `resolveKitchenPacing`.
   */
  readonly kitchenPacing?: KitchenPacingSettings;
  /**
   * Cash-tip loss-prevention policy (Stage 4). Absent/disabled ⇒ no cash-tip
   * reviews are opened. Distinct from `allowTips` (whether tips are accepted at
   * all); governance only acts on cash tips when tips are allowed.
   */
  readonly cashTipGovernance?: CashTipGovernanceSettings;
  /** How cash tips are recorded and paid out; see locations/cashTipSettings.ts. SPARSE, server-written. */
  readonly cashTips?: Partial<LocationCashTipSettings>;
  /**
   * Card holds (pre-auth card on file) for tabs and table service; see
   * locations/cardHoldSettings.ts and docs/CARD_HOLDS.md. SPARSE,
   * server-written. Dark behind `initiative.card_holds`.
   */
  readonly cardHolds?: Partial<LocationCardHoldSettings>;
  /**
   * Restaurant-wide printing settings (ticket appearance, print coordinator,
   * PAID expo chit, launch initiatives). SPARSE: only operator-set keys are
   * stored and an absent key (or block) is the default. Deliberately NOT
   * defaulted here, so the constructor never invents an empty block that a
   * whole-doc write would then persist. A device may not change it (firestore rules,
   * settingsBlocksWriteAllowed). See RestaurantPrintingSettings.
   */
  readonly printing?: LocationPrintingSettings;
  /**
   * Per-restaurant gift-card membership (`programId` — absent = no program,
   * no gift cards here) and switches.
   * SPARSE (absent key = the permissive default)
   * and SERVER-WRITTEN only: no client may change it (firestore rules,
   * serverOnlySettingsBlocks); the writer is
   * `giftCardProgramService.upsertRestaurantSettings`. Not defaulted here, for
   * the same reason as `printing`. See RestaurantGiftCardSettingsBlock.
   */
  readonly giftCards?: LocationGiftCardSettingsBlock;
  /**
   * Appointments booking policy. SPARSE
   * (absent key = the BookingSettings default) and SERVER-WRITTEN only (the
   * writer is `appointmentService.updateBookingSettings`). Its existence means
   * nothing: `features.appointments` decides whether appointments are offered.
   * PUBLIC like the rest of this document. See RestaurantBookingSettingsBlock.
   */
  readonly booking?: LocationBookingSettingsBlock;
  /**
   * Bar spirit lists (dark: `initiative.bar_spirit_lists`) — which generated
   * brand / mixer / style lists are on, the restaurant's own tier, mixer and
   * style prices, and the standard pour. SPARSE (absent key = the
   * `defaultBarSpiritSettings()` value) and SERVER-WRITTEN only: the writer is
   * `BarSpiritListService.saveSettings` (firestore rules,
   * serverOnlySettingsBlocks). Not defaulted here, for the same reason as
   * `printing`. See RestaurantBarSpiritListsBlock.
   */
  readonly barSpiritLists?: LocationBarSpiritListsBlock;
  /**
   * The bar WINE LIST settings — the glass pour and the large pour, which
   * decide what a serving draws from stock. SPARSE and SERVER-WRITTEN only
   * (`BarWineListService.saveSettings`; firestore rules
   * serverOnlySettingsBlocks). Read through `barWineSettingsFromJSON`.
   */
  readonly barWineList?: LocationBarWineListBlock;
  /**
   * LEGACY: the retired owner acknowledgment of the Online Service Fee (spec
   * 2026-10-05 §2). Owner decision 2026-10-06 (option A): the fee applies to
   * every location that takes online orders, so this gates NOTHING and nothing
   * writes it any more. Still server-only in the rules, so a stored value
   * stays inert; kept so existing documents still type-check and hydrate.
   */
  readonly onlineServiceFeeEnrollment?: OnlineServiceFeeEnrollment;
  // NOTE: Tax rates live in the restaurant's `tax_rates` subcollection
  // as they can vary by item type, location, and order type
}

// ========================================
// Google Business Profile Integration Types
// These enable OrderUp to be the source of truth
// ========================================

/**
 * Service options offered by the restaurant
 * Maps directly to Google Business Profile service attributes
 */
export interface LocationServiceOptions {
  readonly dineIn: boolean;
  readonly takeout: boolean;
  readonly delivery: boolean;
  readonly curbsidePickup: boolean;
  readonly noContactDelivery: boolean;
  readonly driveThrough?: boolean;
  readonly outdoorDining?: boolean;
}

/**
 * Parking options available
 */
export type ParkingType =
  | "free"
  | "paid"
  | "street"
  | "valet"
  | "garage"
  | "none";

/**
 * Restaurant attributes and amenities
 * Maps to Google Business Profile attributes
 */
export interface LocationAttributes {
  // Accessibility
  readonly wheelchairAccessibleEntrance?: boolean;
  readonly wheelchairAccessibleSeating?: boolean;
  readonly wheelchairAccessibleRestroom?: boolean;

  // Dining Options
  readonly outdoorSeating?: boolean;
  readonly rooftopSeating?: boolean;
  readonly privateRooms?: boolean;
  readonly patio?: boolean;
  readonly fireplace?: boolean;

  // Amenities
  readonly wifi?: boolean;
  readonly parking?: ParkingType;
  readonly valetParking?: boolean;
  readonly bikeParking?: boolean;
  readonly evCharging?: boolean;

  // Reservations & Planning
  readonly reservationsRequired?: boolean;
  readonly reservationsAccepted?: boolean;
  readonly onlineReservations?: boolean;
  /**
   * External online-reservation booking URL (OpenTable, Resy, Tock, …).
   * The website "Reserve Online" button links here and hides when absent.
   */
  readonly reservationUrl?: string;
  readonly waitlistAvailable?: boolean;

  // Payment Methods
  readonly acceptsCreditCards?: boolean;
  readonly acceptsDebitCards?: boolean;
  readonly acceptsCash?: boolean;
  readonly acceptsNfc?: boolean; // Apple Pay, Google Pay, etc.
  readonly acceptsCrypto?: boolean;

  // Dietary & Menu Features
  readonly vegetarianOptions?: boolean;
  readonly veganOptions?: boolean;
  readonly glutenFreeOptions?: boolean;
  readonly halalOptions?: boolean;
  readonly kosherOptions?: boolean;
  readonly organicOptions?: boolean;

  // Alcohol
  readonly servesAlcohol?: boolean;
  readonly fullBar?: boolean;
  readonly beerAndWine?: boolean;
  readonly byob?: boolean;
  readonly happyHour?: boolean;

  // Entertainment & Atmosphere
  readonly liveMusic?: boolean;
  readonly liveEntertainment?: boolean;
  readonly sportsTv?: boolean;
  readonly danceFloor?: boolean;
  readonly karaoke?: boolean;

  // Family & Groups
  readonly familyFriendly?: boolean;
  readonly kidsMenu?: boolean;
  readonly highChairs?: boolean;
  readonly goodForGroups?: boolean;
  readonly privateEvents?: boolean;

  // Services
  readonly catering?: boolean;
  readonly brunch?: boolean;
  readonly breakfast?: boolean;
  readonly lunch?: boolean;
  readonly dinner?: boolean;
  readonly lateNight?: boolean;

  // Health & Safety
  readonly sanitized?: boolean;
  readonly staffWearsMasks?: boolean;
  readonly temperatureChecks?: boolean;
  readonly contactlessPayment?: boolean;
}

/**
 * Special hours for holidays, events, or temporary changes
 */
export interface SpecialHours {
  readonly date: string; // YYYY-MM-DD format
  readonly openTime?: string; // HH:MM (24hr) - undefined if closed
  readonly closeTime?: string; // HH:MM (24hr) - undefined if closed
  readonly isClosed: boolean;
  readonly reason?: string; // "Holiday", "Private Event", "Renovation", etc.
}

/**
 * Geographic coordinates of a location.
 */
export interface GeoCoordinates {
  readonly latitude: number;
  readonly longitude: number;
  readonly placeId?: string; // Google Place ID if known
}

/**
 * Business categories - supports multiple categories like Google Business
 * Primary category should be first in the array
 */
export type RestaurantCategory = string; // e.g., "American restaurant", "Pizza restaurant"

export interface PlatformSyncConfig {
  readonly enabled: boolean;
  readonly connectionId?: string; // UUID-based connection identifier
  readonly accountId?: string;
  readonly businessId?: string;
  readonly pageId?: string;
  /** The platform's own location id (shared by every platform — not Google-specific). */
  readonly platformLocationId?: string;
  /** @deprecated legacy stored name — read via readRenamed; removed in Phase 0 contract */
  readonly locationId?: string; // location-word-ok: legacy name (Phase 0 contract)
  readonly accessToken?: string; // OAuth access token
  readonly refreshToken?: string; // OAuth refresh token
  readonly tokenExpiresAt?: Date; // Token expiration date
  readonly syncStatus?:
    | "idle"
    | "syncing"
    | "error"
    | "completed"
    | "connected";
  readonly aiConfig?: {
    readonly enabled: boolean;
    readonly responseStyle?: string;
    readonly autoReply?: boolean;
    readonly responseLength?: "short" | "medium" | "long";
    readonly includeBusinessName?: boolean;
    readonly minimumRating?: number;
    readonly maxResponseTime?: number;
    readonly businessVoice?: string;
  };
  readonly lastSync?: Date;
  readonly lastError?: string;
}

export interface GoogleBusinessAIConfig {
  readonly enabled: boolean;
  readonly responseStyle?: string;
  readonly autoReply?: boolean;
  readonly responseLength?: "short" | "medium" | "long";
  readonly includeBusinessName?: boolean;
  readonly minimumRating?: number;
  readonly maxResponseTime?: number;
  readonly businessVoice?: string;
}

export interface GoogleBusinessSyncConfig extends PlatformSyncConfig {
  readonly aiConfig?: GoogleBusinessAIConfig;
}

/**
 * Google Places API interfaces removed - migrated to Google Business Profile API
 * All Google Places functionality has been replaced with Google Business Profile integration
 * See GoogleBusinessProfile interface below for the new data structure
 */

/**
 * Google Business Profile - Synced profile data stored in Firestore
 * This replaces live Google Places API calls with cached data
 */
export interface GoogleBusinessProfile {
  readonly googleLocationId: string; // Google Business location ID (locations/{id})
  /** @deprecated legacy stored name — read via readRenamed; removed in Phase 0 contract */
  readonly locationId?: string; // location-word-ok: legacy name (Phase 0 contract)
  readonly name: string; // Business name
  readonly description?: string; // Business description
  readonly address: {
    readonly street: string;
    readonly city: string;
    readonly state: string;
    readonly postalCode: string;
    readonly country: string;
    readonly formatted: string;
  };
  readonly phone: string; // Primary phone number
  readonly additionalPhones?: readonly string[]; // Additional phone numbers
  readonly website?: string; // Website URL
  readonly coordinates?: {
    readonly latitude: number;
    readonly longitude: number;
  };
  readonly hours: {
    readonly regularHours: ReadonlyArray<{
      readonly openDay: string; // e.g., "MONDAY"
      readonly openTime: string; // e.g., "09:00"
      readonly closeDay: string; // e.g., "MONDAY"
      readonly closeTime: string; // e.g., "17:00"
    }>;
    readonly specialHours?: ReadonlyArray<{
      readonly startDate: Date;
      readonly openTime: string;
      readonly closeTime: string;
      readonly isClosed: boolean;
    }>;
  };
  readonly rating?: number; // Average rating (1-5)
  readonly reviewCount?: number; // Total number of reviews
  readonly responseRate?: number; // Calculated response rate (0-100)
  readonly profileCompleteness?: number; // Calculated profile completeness score (0-100)
  readonly missingFields?: readonly string[]; // List of fields improving the score
  readonly categories: readonly string[]; // Business categories
  /**
   * Neighbourhoods and towns this business serves, as declared on its Google
   * Business Profile (`serviceArea.places.placeInfos`).
   *
   * Mirrored here deliberately. The authoritative copy lives on the INTEGRATION
   * document, which also carries live OAuth tokens — not something a public,
   * unauthenticated render may read for a neighbourhood name. This projection
   * holds the two harmless fields and nothing else.
   */
  /**
   * Google's own "write a review" link for this location
   * (`metadata.newReviewUri`).
   *
   * Mirrored rather than CONSTRUCTED. The usual trick is to build
   * `search.google.com/local/writereview?placeid=…`, which needs a Place ID we
   * do not hold — `GeoCoordinates.placeId` exists on the model and nothing
   * has ever populated it — and which is a URL shape Google has never promised.
   * This is the value the API hands us for exactly this purpose, so it cannot
   * drift and needs no second identifier.
   *
   * Absent until the profile syncs, and absent for a location Google does not
   * offer reviews on. A review request with no link is not sendable.
   */
  readonly newReviewUri?: string;
  /** Google Place ID, as Google reports it for this location. */
  readonly placeId?: string;
  readonly serviceAreas?: ReadonlyArray<{
    /** As Google renders it — "New Braunfels, TX". */
    readonly name: string;
    /** Google's place id, so a rename is not a new place. */
    readonly placeId: string;
  }>;
  readonly verified: boolean; // Whether business is verified
  readonly mapsUri: string; // Google Maps URL
  readonly lastSynced: Date; // When this data was last synced
  readonly lastMediaSync?: Date; // When media was last synced (photos/videos stored in subcollection)
}

/**
 * Firestore → model for the synced Google Business Profile. Docs written before
 * the Phase 0 rename carry only the legacy field name; the new
 * `googleLocationId` wins.
 */
export function readGoogleBusinessProfile(
  raw: unknown,
  where: string,
): GoogleBusinessProfile | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const doc = raw as Record<string, unknown>;
  const googleLocationId = readRenamed<string>(doc, "googleLocationId", "locationId", where); // location-word-ok: legacy name (Phase 0 contract)
  return (googleLocationId === undefined
    ? doc
    : { ...doc, googleLocationId }) as unknown as GoogleBusinessProfile;
}

export interface LocationPlatformSync {
  readonly enabled: boolean;
  readonly autoSync: boolean;
  readonly syncFrequency: "hourly" | "daily" | "weekly";
  readonly platforms: {
    readonly googleMyBusiness?: GoogleBusinessSyncConfig;
    readonly yelp?: PlatformSyncConfig;
    readonly facebook?: PlatformSyncConfig;
    readonly instagram?: PlatformSyncConfig;
    readonly appleMaps?: PlatformSyncConfig;
    readonly doordash?: PlatformSyncConfig;
    readonly ubereats?: PlatformSyncConfig;
  };
}

/**
 * One owner-selected font slot. `curated` references a key in the shared font
 * registry (loaded by the renderer via next/font); `custom` is an uploaded
 * font file (the renderer injects an `@font-face` for `family` from `url`).
 */
export interface LocationFontChoice {
  readonly source: "curated" | "custom";
  /** Curated: registry key. Custom: the CSS font-family name to apply. */
  readonly family: string;
  /** Custom only: woff2/ttf URL in storage. */
  readonly url?: string;
}

/** Max photos in `branding.heroSlides` — shared by schema, builder and renderer. */
export const HERO_SLIDES_MAX = 6;

/** Owner-authored Spotlight card (dine-in Style 2). */
export interface LocationSpotlight {
  readonly heading?: string;
  readonly body?: string;
  readonly buttonLabel?: string;
  readonly buttonUrl?: string;
  readonly image?: string | OptimizedImage;
}

export interface LocationBranding {
  readonly logo?: string | OptimizedImage; // Full logo for header use
  readonly logoMode?: "image" | "text"; // Header display: logo image or business name text
  /**
   * Hero heading display: `'logo'` renders the uploaded logo image in place of
   * the text headline (the headline stays in the DOM as sr-only text for
   * SEO/screen readers). Absent/`'headline'` = text headline (default). The
   * renderer falls back to the text headline whenever no logo is uploaded.
   */
  readonly heroDisplay?: "headline" | "logo";
  readonly icon?: string | OptimizedImage; // Icon/favicon with circle avatar styling
  readonly heroImage?: string | OptimizedImage;
  readonly heroImageOpacity?: number; // Overlay darkness 0-100 (0 = no overlay, 100 = fully dark)
  /**
   * Hero "Welcome to" eyebrow message on the dine-in Style-1 (bold) template.
   * Owner-editable; defaults to "Welcome to" when unset, hidden when set to an
   * empty string.
   */
  readonly heroWelcomeText?: string;
  /**
   * Size preset for the hero "Welcome to" eyebrow (dine-in Style-1). Each preset
   * scales responsively — the value is the desktop reference and mobile scales
   * down. Defaults to "xl" (the built-in preset).
   */
  readonly heroWelcomeSize?: "sm" | "md" | "lg" | "xl";
  readonly bannerImage?: string | OptimizedImage; // Banner image for page headers
  readonly backgroundImage?: string | OptimizedImage; // Background texture/pattern
  /**
   * Dedicated photo for the menu-preview section (the image panel beside the
   * "Our Menu" intro on dine-in, fine-dining, pizzeria, bar-lounge, takeout,
   * cafe-bakery, fast-casual and food-truck templates). Renderer chain:
   * menuPreviewImage → supplementaryImages[0] (legacy) → `menu-preview`
   * content-block image → panel hidden (text centers). Replaces the old
   * initial-letter placeholder.
   */
  readonly menuPreviewImage?: string | OptimizedImage;
  /**
   * Dedicated photo for the "Our Story" split band on the dine-in Style-1 (bold)
   * template. Its own slot so it's independent of the Welcome section's banner;
   * the renderer falls back to `backgroundImage` → `supplementaryImages[2]` →
   * `bannerImage` when unset.
   */
  readonly storyImage?: string | OptimizedImage;
  /**
   * A standalone full-width photo band shown below the story section on the
   * dine-in Style-1 (bold) template — just the photo, no text. Its own slot so
   * owners can add a clean image break. Section hides when unset.
   */
  readonly featureImage?: string | OptimizedImage;
  readonly supplementaryImages?: readonly (string | OptimizedImage)[]; // Niche/supplementary images
  /**
   * Ordered hero slideshow photos (max {@link HERO_SLIDES_MAX}) for the dine-in
   * Style-2 (editorial) template. Absent/empty → the renderer falls back to
   * `heroImage` followed by the owner's visible gallery photos. Other
   * templates ignore it.
   */
  readonly heroSlides?: readonly (string | OptimizedImage)[];
  /**
   * Owner override for the dine-in Style-2 "Spotlight" callout card. Ignored
   * unless `heading` is non-empty; otherwise the card fills itself (active
   * announcement → reservations → online ordering → hidden).
   */
  readonly spotlight?: LocationSpotlight;
  /**
   * Dine-in Style-2 is monochrome by default; `true` lets the owner's brand
   * color onto its buttons, heading rules and link hover. Absent/false =
   * monochrome. Every other template/style ignores it.
   */
  readonly useBrandColors?: boolean;
  /**
   * Owner-selected site typography, applied across the whole site (heading
   * elements + body). Each slot is either a CURATED font (a key from the
   * shared font registry, e.g. `"oswald"`) or a CUSTOM uploaded font (family
   * name + the woff2/ttf URL in storage). Absent → the template's default
   * serif/sans, so existing sites are unchanged.
   */
  readonly fonts?: {
    readonly heading?: LocationFontChoice;
    readonly body?: LocationFontChoice;
  };
  readonly colors: {
    readonly primary: string;
    readonly secondary: string;
    readonly accent: string;
    readonly primaryOpacity?: number;
    readonly secondaryOpacity?: number;
    readonly accentOpacity?: number;
    readonly buttonColor?: string;
    readonly buttonOpacity?: number;
    /**
     * Explicit owner intent: `true` once the owner has saved any brand-color
     * change in the builder. Template families substitute their tuned default
     * palettes ONLY while this is not `true` and the colors still equal the
     * creation defaults (see `isDefaultBrandColors` in apps/restaurant) — so a
     * deliberate choice of the default hexes is respected once customized.
     * Absent on docs that predate the flag (legacy substitution behavior).
     */
    readonly customized?: boolean;
  };
}

/**
 * Price range using standard $ symbols
 * Based on average entrée price:
 * - '$' = Under $15 (Fast food, quick service)
 * - '$$' = $15-30 (Casual dining)
 * - '$$$' = $31-60 (Upscale casual)
 * - '$$$$' = Over $60 (Fine dining)
 */
export type PriceRangeSymbol = "$" | "$$" | "$$$" | "$$$$";

/**
 * Detailed price metrics calculated from menu items
 * Used for accurate SEO and customer expectations
 */
export interface LocationPriceMetrics {
  /** The display symbol ($, $$, $$$, $$$$) */
  readonly priceRange: PriceRangeSymbol;
  /** Average price of all menu items */
  readonly averagePrice: number;
  /** Median price (more resistant to outliers) */
  readonly medianPrice: number;
  /** Lowest priced item */
  readonly minPrice: number;
  /** Highest priced item */
  readonly maxPrice: number;
  /** Number of items analyzed */
  readonly itemCount: number;
  /** When this was last calculated */
  readonly calculatedAt: Date;
}

export interface LocationSEO {
  readonly title: string;
  readonly description: string;
  readonly keywords: readonly string[];
  readonly ogImage?: string;
  /**
   * Price range can be:
   * - Manually set by owner
   * - Calculated from menu (stored in priceMetrics)
   * - Null if not determined
   */
  readonly priceRange?: PriceRangeSymbol;
  /** Detailed price metrics from menu analysis */
  readonly priceMetrics?: LocationPriceMetrics;
  /**
   * Site verification codes for search engines and analytics
   * Set these to enable ownership verification in Search Console, etc.
   */
  /**
   * Which GENERATED page families this site publishes.
   *
   * These are URL spaces built from the restaurant's own catalog rather than
   * route files — `/menu/<dish>`, `/tags/<tag>`. Absent means PUBLISHED: the
   * pages are the product, and an existing site that never opts in still gets
   * them. An operator who does not want a page per dish turns that family off
   * here, and the routes 404 while the sitemap stops listing them.
   */
  readonly generatedPages?: {
    /** One page per orderable dish. Default true. */
    readonly menuItems?: boolean;
    /** One page per dish tag and dietary label. Default true. */
    readonly tags?: boolean;
    /** One page per declared Google service area. Default true. */
    readonly places?: boolean;
  };
  readonly verification?: {
    /** Google Search Console verification code */
    readonly google?: string;
    /** Bing Webmaster Tools verification code */
    readonly bing?: string;
    /** Yandex Webmaster verification code */
    readonly yandex?: string;
    /** Pinterest domain claim verification */
    readonly pinterest?: string;
  };
}

export interface LocationContent {
  readonly aboutText: string;
  readonly specialOffers: readonly string[];
  readonly announcement?: string;
}

export interface LocationSocial {
  readonly facebook?: string;
  readonly instagram?: string;
  readonly twitter?: string;
  readonly tiktok?: string;
}

export interface LocationOrderingIntegration {
  /**
   * @deprecated NOT a source of truth, and specifically NOT evidence that an
   * operator disabled ordering. `buildWebsiteConfig` manufactures
   * `enabled: false` for EVERY restaurant that has any `websiteConfig` (the
   * block below is unconditional, unlike its `contactForm`/`featuredItems`
   * siblings which are guarded), and the website builder's first draft-seed
   * write then persists that manufactured value. A stored `false` therefore
   * means "this doc passed through hydration", not "the merchant turned
   * ordering off".
   *
   * The single source of truth for "first-party online ordering is live" is
   * `Restaurant.onlineOrderingEnabled`, which is what the server actually
   * enforces (`restaurantService.isAcceptingOrders`). Nothing may gate on this
   * field, and nothing may key a migration on it — doing so would disable
   * order intake and delist from discovery across essentially every tenant.
   *
   * Retained (and still required) ONLY so stored documents keep parsing:
   * `RestaurantOrderingIntegrationSchema` is `.strict()` with `enabled`
   * required and no default, so dropping the key fails every existing doc on
   * its next save.
   */
  readonly enabled: boolean;
  readonly provider:
    | "internal"
    | "doordash"
    | "ubereats"
    | "grubhub"
    | "custom";
  readonly url?: string;
  readonly buttonText: string;
  readonly buttonColor: string;
  readonly config?: LocationOrderingConfig;
}

export interface LocationOrderingConfig {
  // No prep time here: how long the kitchen takes is restaurant-wide —
  // `settings.estimatedPrepTimeMinutes` via `resolvePrepLeadMinutes`, paced by
  // `quotePrepTime` (docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md).
  /** Minimum lead time in minutes (time between order placement and pickup/delivery) */
  readonly minLeadTimeMinutes: number;
  /** Time slot interval in minutes (e.g., 15, 30, 60) */
  readonly timeSlotIntervalMinutes: number;
  /** Buffer time before closing in minutes (stop accepting orders X minutes before close) */
  readonly closingBufferMinutes: number;
  /** Whether ASAP ordering is enabled */
  readonly enableAsapOrders: boolean;
  /**
   * Whether delivery is enabled.
   *
   * NOTE (data-contract): fulfillment gating is canonically driven by the
   * top-level `Restaurant.deliveryEnabled` / `onlineOrderingEnabled` flags
   * (Takeout & Delivery surface). These two booleans are written by the
   * website builder's fulfillment toggle and no longer feed any renderer
   * (the SEO JSON-LD now derives from the canonical flags). Slated for
   * removal once the builder toggle is retired — see the wc/t3-contract note.
   */
  readonly enableDelivery: boolean;
  /** Whether pickup is enabled. See `enableDelivery` note above. */
  readonly enablePickup: boolean;
}

/**
 * Per-restaurant web-analytics / marketing-pixel IDs injected into the public
 * restaurant site `<head>`. Lets each restaurant measure their own traffic and
 * ad conversions on their marketing site without a shared platform property.
 */
export interface LocationWebsiteAnalytics {
  /** Google Analytics 4 Measurement ID (e.g. "G-XXXXXXX"). */
  readonly ga4MeasurementId?: string;
  /** Meta (Facebook) Pixel ID for ad conversion tracking. */
  readonly metaPixelId?: string;
  /** Google Tag Manager container ID (e.g. "GTM-XXXXXX"). */
  readonly gtmId?: string;
}

export interface LocationContentBlock {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly image?: string; // JSON string of OptimizedImage or URL
  readonly icon?: string; // Icon name (e.g., 'Heart', 'ChefHat')
  readonly layout: "image-left" | "image-right" | "centered" | "text-only";
  readonly backgroundColor?: string;
  readonly textColor?: string;
  readonly instanceId?: string; // Instance identifier for multiple content block sections
}

/** Per-page SEO overrides for an owner-created custom page. */
export interface LocationCustomPageSeo {
  readonly title?: string;
  readonly description?: string;
}

/**
 * An owner-created standalone page, beyond the fixed home/menu/gallery/contact/
 * story routes. A custom page is a titled, ordered collection of the SAME
 * `RestaurantContentBlock`s used everywhere else — no new content model. It is
 * addressed by `slug` (URL-safe, unique per restaurant, never a reserved
 * route — see `@order-up/models` CustomPages helpers) and rendered on the live
 * site by `apps/restaurant/src/app/[slug]/[page]`.
 */
export interface LocationCustomPage {
  readonly id: string;
  /** URL-safe path segment, e.g. "private-events". Unique per restaurant. */
  readonly slug: string;
  /** Owner-facing page title, also the default `<h1>` and nav label. */
  readonly title: string;
  /** Published gate — only enabled pages render on the live site. */
  readonly enabled: boolean;
  /** Optional nav-link label; falls back to `title` when absent. */
  readonly navLabel?: string;
  /** When true, the page appears in the shared header/footer nav. */
  readonly showInNav?: boolean;
  /** The page body — reuses the shared content-block model + renderer. */
  readonly blocks: readonly LocationContentBlock[];
  /** Optional `<title>` / description overrides; else derived from `title`. */
  readonly seo?: LocationCustomPageSeo;
}

export type CtaImageShape =
  | "none"
  | "circle"
  | "house"
  | "hexagon"
  | "diamond"
  | "arch"
  | "shield"
  | "leaf"
  | "octagon"
  | "star"
  | "cross"
  | "drop";

export interface LocationCtaSection {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly backgroundImage?: string; // JSON string of OptimizedImage or URL
  readonly instanceId?: string; // Instance identifier for multiple CTA sections
  readonly imageShape?: CtaImageShape; // Decorative clip-path shape for the CTA image
}

/** A configurable process step (e.g. "How It Works" section) */
export interface LocationProcessStep {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly icon?: string; // lucide icon name
  readonly image?: string; // URL or OptimizedImage JSON
}

/** A configurable service feature (e.g. "Our Features" section) */
export interface LocationServiceFeature {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly icon?: string; // lucide icon name
}

/**
 * Canonical set of website layout templates — the SINGLE SOURCE OF TRUTH.
 *
 * Consumed by the Zod schema (`z.enum(WEBSITE_LAYOUT_TEMPLATES)`), the API
 * hosting `_shared.ts` JSON-Schema fragments, and the admin template dialog.
 * Keep in lockstep with the SQL CHECK on `hosting.website_templates`
 * (`apps/supabase/.../phase_11_hosting_foundation.sql`) — the only mirror that
 * cannot import this const (submodule); adding a value here requires a matching
 * SQL migration.
 */
export const WEBSITE_LAYOUT_TEMPLATES = [
  "multi-location",
  "dine-in",
  "takeout",
  "fine-dining",
  "cafe-bakery",
  "food-truck",
  "bar-lounge",
  "fast-casual",
  "pizzeria",
  "cleaning-service",
] as const;

export type WebsiteLayoutTemplate = (typeof WEBSITE_LAYOUT_TEMPLATES)[number];

/** Visual style variant within a template family. Single source of truth. */
export const WEBSITE_TEMPLATE_STYLES = ["1", "2", "3"] as const;

export type WebsiteTemplateStyle = (typeof WEBSITE_TEMPLATE_STYLES)[number];

/** A menu group reference for website display with visibility toggle */
export interface WebsiteMenuSection {
  readonly menuGroupId: number;
  readonly name: string;
  readonly visible: boolean;
  readonly position: number;
}

/** A gallery photo with per-image visibility, caption, and ordering */
export interface GalleryPhoto {
  readonly id: string;
  readonly image: string | OptimizedImage;
  readonly caption?: string;
  readonly visible: boolean;
  readonly position: number;
}

/**
 * A manually-curated Instagram-style post. Used when `instagramFeed.mode` is
 * `'manual'` (or as the fallback when an auto-sync connection is empty/errored).
 * Mirrors {@link GalleryPhoto} with an added post `link`.
 */
export interface InstagramManualPost {
  readonly id: string;
  readonly image: string | OptimizedImage;
  readonly caption?: string;
  /** Deep-link to the Instagram post (opened when the tile is clicked). */
  readonly link?: string;
  readonly visible: boolean;
  readonly position: number;
}

/**
 * Website-facing Instagram-feed configuration (the section on/off + how it's
 * sourced). The OAuth CONNECTION + token live in the top-level `integrations`
 * collection, and auto-synced posts are cached in the `instagram_feed`
 * subcollection — NOT here. This object only holds what the builder edits.
 */
export interface InstagramFeedConfig {
  /** Show/hide the feed section on the site (default false — ships dark). */
  readonly enabled?: boolean;
  /** `auto` = OAuth-synced posts; `manual` = owner-curated `manualPosts`. */
  readonly mode?: "auto" | "manual";
  /** Max tiles to render (3–12, default 8). */
  readonly displayCount?: number;
  /** Curated posts used when `mode === 'manual'` (or as an auto fallback). */
  readonly manualPosts?: readonly InstagramManualPost[];
  /** Optional eyebrow above the section, e.g. "@houseofpho". */
  readonly headingEyebrow?: string;
}

/** Configuration for the website contact form */
export interface ContactFormConfig {
  readonly enabled: boolean;
  readonly heading?: string;
  readonly subheading?: string;
  readonly successMessage?: string;
}

/**
 * Curated featured menu items surfaced on the public marketing site.
 *
 * Write target for the featured-selection track: an operator hand-pick
 * (`manual`), a product-mix / best-seller derivation (`product-mix`), or an
 * AI-generated pick (`ai`). When present and non-empty, it is the highest-
 * priority source the site's FeaturedItemsEmbed honors (it falls back to
 * `discoveryInfo` flags, then a legacy "Featured"/"Popular" group).
 *
 * `menuItemGuids` are `Item.guid` values (NOT order-item guids), in intended
 * display order. `blurbs` is an optional per-item marketing description keyed by
 * the same `Item.guid`.
 */
export interface LocationFeaturedItems {
  readonly menuItemGuids: readonly string[];
  readonly source: "manual" | "product-mix" | "ai";
  readonly blurbs?: Readonly<Record<string, string>>;
  readonly generatedAt?: string; // ISO timestamp of when the selection was produced
}

export interface LocationWebsiteConfig {
  readonly enabled: boolean;
  readonly parentMenuGroupId?: number; // Auto-select this menu for customer-facing sites
  readonly layoutTemplate?: WebsiteLayoutTemplate; // Selected website layout template
  readonly templateStyle?: WebsiteTemplateStyle; // Visual style variant within template category (default '1')
  /** Curated-template provenance written by the hosting select-template route. */
  readonly templateKey?: string;
  /** UID/email of the last hosting-surface editor (provenance for the SQL mirror). */
  readonly lastEditedBy?: string;
  /** Timestamp of the last hosting-surface edit (provenance for the SQL mirror). */
  readonly lastEditedAt?: string | Date;
  readonly branding: LocationBranding;
  readonly seo: LocationSEO;
  readonly content: LocationContent;
  readonly contentBlocks?: readonly LocationContentBlock[]; // Configurable content blocks
  readonly ctaSections?: readonly LocationCtaSection[]; // Configurable CTA sections
  readonly customPages?: readonly LocationCustomPage[]; // Owner-created standalone pages (slug + blocks)
  readonly processSteps?: readonly LocationProcessStep[]; // Configurable "How It Works" steps
  readonly processStepsTitle?: string; // Custom heading for process steps section
  readonly serviceFeatures?: readonly LocationServiceFeature[]; // Configurable service feature cards
  readonly serviceFeaturesTitle?: string; // Custom heading for service features section
  readonly serviceFeaturesSubtitle?: string; // Custom subtitle for service features section
  readonly galleryEnabled?: boolean; // Show/hide gallery section on website (default true)
  /**
   * Show the newsletter signup section on the website (default false).
   * Exception: the multi-location family showed it unconditionally before this
   * flag existed, so its renderer treats ABSENT as enabled (legacy behavior);
   * an explicit `false` hides it everywhere.
   */
  readonly newsletterEnabled?: boolean;
  readonly galleryImages?: readonly (string | OptimizedImage)[]; // Legacy: raw image array
  readonly galleryPhotos?: readonly GalleryPhoto[]; // Gallery photos with per-photo controls
  readonly menuSections?: readonly WebsiteMenuSection[]; // Menu groups with visibility toggles
  readonly contactForm?: ContactFormConfig; // Contact form configuration
  readonly featuredItems?: LocationFeaturedItems; // Curated featured menu items for the marketing site
  readonly social: LocationSocial;
  readonly ordering: LocationOrderingIntegration;
  /** Per-restaurant web-analytics / marketing-pixel IDs (GA4, Meta Pixel, GTM). */
  readonly analytics?: LocationWebsiteAnalytics;
  /** First-party Instagram feed section config (section on/off, mode, curated posts). */
  readonly instagramFeed?: InstagramFeedConfig;
}

export type LocationStatus = "active" | "inactive";

/**
 * Per-restaurant TRX gateway credentials. Required when the restaurant's
 * payment routing selects TRX as the effective provider; rejected by
 * cross-field validation otherwise (see
 * `validateProviderCredentialConsistency` in
 * `@order-up/schemas/payments/PaymentRouting`).
 *
 * Six fields, three subgroups by consumer:
 *
 *   1. Gateway HTTP Basic auth (used by the API server's `TrxHttpClient`):
 *      - `clientId`  — TRX merchant client identifier (zero-padded display
 *                      string, e.g. `"001248"`). Forms the username half
 *                      of Basic auth as `${clientId}_${sourceId}`.
 *      - `sourceId`  — TRX merchant source identifier.
 *      - `password`  — gateway password.
 *
 *   2. Hosted PayPage / JS library checkout (used by `paypage-init`
 *      route to mint sessions for `apps/checkout`):
 *      - `payPageGuid` — identifier of the PayPage provisioned in the
 *                        TRX dev portal that the JS-library flow mints
 *                        sessions against.
 *
 *   3. Mobile SDK request encryption (legacy slots — not consumed by
 *      any current TRX runtime path; kept optional on the interface so
 *      existing Firestore docs that carry these fields don't fail to
 *      hydrate. If TRX issues per-merchant SDK encryption material in
 *      the future, populate here):
 *      - `key`  — optional symmetric request-encryption key.
 *      - `iv`   — optional initialization vector for the symmetric key.
 *
 * Required at provisioning: `clientId`, `sourceId`, `password` — the
 * three fields TRX issues during merchant onboarding. `payPageGuid` is
 * required only for hosted-tokenization web checkout and is filled in
 * once a PayPage is configured in the TRX portal; the per-restaurant
 * provisioning validator does not require it.
 *
 * TODO(trx-kms): `password` and `payPageGuid` are stored plaintext
 * during the validation phase of the per-restaurant migration. Move to
 * GCP KMS envelope encryption per the deferred plan in
 * `docs/2026-04-23-TRX_MIGRATION.md` once the per-restaurant flow is
 * validated end-to-end. `clientId` and `sourceId` are identifiers
 * and remain plaintext.
 */
export interface TrxCredentials {
  readonly clientId: string;
  readonly sourceId: string;
  readonly password: string;
  readonly payPageGuid?: string;
  readonly key?: string;
  readonly iv?: string;
}

/**
 * The NON-SECRET half of {@link TrxCredentials} — safe to hydrate onto the
 * client-facing model.
 *
 * `trxCredentials` itself is deliberately never mapped in `fromJSON`, because
 * `password`/`key`/`iv` must not ride along on a model shared by checkout,
 * ordering and POS. But dropping the WHOLE object meant the admin app — which
 * legitimately configures these — re-opened a saved restaurant to blank fields
 * and could no longer tell TRX was configured at all, let alone edit it.
 *
 * So we surface the identifiers (which are not secrets: `payPageGuid` is
 * literally embedded in the checkout iframe URL) plus a presence flag for the
 * secret. The password never leaves Firestore/the server; the admin re-types it
 * only when it actually changes.
 *
 * Contract phase: this projection is now PERSISTED on the restaurant doc (and
 * declared on {@link ILocation} + `RestaurantSchema`), because the doc no
 * longer carries `trxCredentials` at all — the secret lives only in the sealed
 * payment-credentials vault. `hasPassword` therefore reflects the VAULT's
 * stored secret, and is the presence signal validators + privileged UI use.
 */
export interface TrxCredentialsPublic {
  readonly clientId: string;
  readonly sourceId: string;
  readonly payPageGuid?: string;
  /** True when a TRX password is stored on the doc. The value is never sent. */
  readonly hasPassword: boolean;
}

/** Build the non-secret view of a raw `trxCredentials` map (server or Firestore shape). */
export function toTrxCredentialsPublic(
  raw: Partial<TrxCredentials> | undefined | null,
): TrxCredentialsPublic | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const clientId = raw.clientId ?? "";
  const sourceId = raw.sourceId ?? "";
  const payPageGuid = raw.payPageGuid;
  const hasPassword = typeof raw.password === "string" && raw.password.length > 0;
  // Nothing identifiable and no stored secret → treat as "not configured".
  if (!clientId && !sourceId && !hasPassword) return undefined;
  return {
    clientId,
    sourceId,
    ...(payPageGuid !== undefined && { payPageGuid }),
    hasPassword,
  };
}

/**
 * Per-rail merchant CAPABILITY — the record of what the PROCESSOR underwrote
 * and what OrderUp HQ activated at go-live. Non-secret and safe to hydrate onto
 * the client-facing model (it carries no identifiers and no credentials), which
 * is why it follows the {@link TrxCredentialsPublic} precedent: a small,
 * platform-controlled, PERSISTED projection declared on {@link ILocation} and
 * mirrored in the `.strict()` `RestaurantSchema`.
 *
 * Until this existed there was exactly ONE payment signal — "routing selects a
 * provider AND credentials are present" — and it was all-or-nothing, so the
 * product could not tell a merchant approved for CARD apart from one also
 * approved for ACH. TRX underwrites the two rails independently (see
 * {@link MerchantExpectedActivity} in `merchant-onboarding/MerchantApplication`),
 * so the platform needs to record them independently.
 *
 * What this is NOT — three neighbouring concepts it must never be conflated
 * with, each of which answers a different question:
 *
 *   1. NOT ROUTING. `providerRouting.providerByRail` decides WHICH provider
 *      handles a rail (`{ Card: "trx", Ach: "trx" }`). Do not overload
 *      it to mean "approved": a routed rail can be un-underwritten, and an
 *      approved rail can be unrouted.
 *   2. NOT INTENT. `MerchantExpectedActivity.acceptCreditCards` / `.acceptAch`
 *      is what the merchant ASKED FOR on the onboarding questionnaire. This
 *      field is what came back approved and was switched on.
 *   3. NOT AN OPERATOR SWITCH. It is a PREREQUISITE, not a second master
 *      toggle. In particular the ACH rail flag does NOT enable Books ACH on its
 *      own — `book_settings.customer_ach_enabled` (mirrored as
 *      `IBooksSettings.customerAchEnabled`) remains the fail-closed operator
 *      opt-in read by every ACH gate. Effective ACH capability is therefore:
 *
 *          approvedRails.ach
 *            AND book_settings.customer_ach_enabled
 *            AND the counsel-approved SEC-code gate for the scope
 *
 *      (see `docs/ACH_POA_ARCHITECTURE.md`). No competing ACH switch lives
 *      here, and none may be added.
 *
 * WRITER CONTRACT: the only writer is OrderUp HQ's activation path, server-side
 * via the Admin SDK. It is deliberately absent from every tenant-facing edit
 * surface. It is a RECORD of underwriting, not a security boundary on its own —
 * the restaurant doc is owner/manager-writable by Firestore rules with no
 * field-level gate (`match /restaurants/{restaurantDocId}` in // scoped-path-ok: prose
 * `firestore-rules/00-legacy.rules`), so every money-moving gate must keep
 * checking its own fail-closed switch in addition to this flag.
 *
 * FAIL CLOSED: an absent `approvedRails` means "nothing approved yet", never
 * "both rails approved". Read it through {@link isRailApproved}, which denies
 * on absent/malformed input.
 */
export interface MerchantApprovedRails {
  /** Credit/debit card acceptance was underwritten and activated. */
  readonly card: boolean;
  /** ACH acceptance was underwritten and activated (a PREREQUISITE — see above). */
  readonly ach: boolean;
}

/**
 * Normalize a raw `approvedRails` map (Firestore or API shape) into the
 * projection. FAIL-CLOSED by construction: only the literal boolean `true`
 * grants a rail, so a legacy/typo'd value (`"true"`, `1`, `{}`) reads as DENY
 * rather than silently approving a merchant for a rail nobody underwrote.
 * Returns `undefined` when nothing was recorded, so an unapproved restaurant
 * keeps the field ABSENT instead of gaining a materialized all-false map.
 */
export function toMerchantApprovedRails(
  raw: unknown,
): MerchantApprovedRails | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const bag = raw as Partial<Record<keyof MerchantApprovedRails, unknown>>;
  return { card: bag.card === true, ach: bag.ach === true };
}

/**
 * The ONLY sanctioned way to ask "is this rail approved?". Denies when the
 * projection is absent (not yet activated) or the flag is anything other than
 * `true` — callers must never hand-roll `approvedRails?.ach !== false`, which
 * inverts the default to ALLOW.
 */
export function isRailApproved(
  approvedRails: MerchantApprovedRails | undefined,
  rail: keyof MerchantApprovedRails,
): boolean {
  return approvedRails?.[rail] === true;
}

/**
 * Operator-facing labels for the rails that are switched on, in a stable order
 * (card first — it is the rail almost every merchant boards for).
 *
 * ONE home for this phrasing so the go-live in-app notification and the go-live
 * email can never disagree about what was enabled. Reads through
 * {@link isRailApproved}, so it inherits the fail-closed default: an absent or
 * malformed projection yields an EMPTY list rather than claiming both rails.
 * Callers join it themselves (`.join(" and ")` in prose, one bullet per entry
 * in HTML).
 */
export function describeApprovedRails(
  approvedRails: MerchantApprovedRails | undefined,
): string[] {
  const labels: string[] = [];
  if (isRailApproved(approvedRails, "card")) labels.push("card payments");
  if (isRailApproved(approvedRails, "ach")) labels.push("bank transfers (ACH)");
  return labels;
}

function toDateOrUndefined(v: unknown): Date | undefined {
  if (v instanceof Date) return v;
  if (v && typeof v === "object" && typeof (v as { toDate?: unknown }).toDate === "function") {
    return (v as { toDate: () => Date }).toDate();
  }
  if (typeof v === "string" || typeof v === "number") return new Date(v);
  return undefined;
}

/** Stored review auto-reply settings, with Firestore Timestamps turned back into Dates. */
function hydrateReviewAutoReply(raw: unknown): LocationReviewAutoReply | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const card = r.voiceCard as Record<string, unknown> | undefined;
  return {
    ...(r as LocationReviewAutoReply),
    activatedAt: toDateOrUndefined(r.activatedAt),
    voiceCard: card
      ? ({ ...card, generatedAt: toDateOrUndefined(card.generatedAt) } as unknown as ReviewVoiceCard)
      : undefined,
  };
}

/** How a location's review replies sound; the AI writes it from the owner's own replies. */
export interface ReviewVoiceCard {
  greeting: string;
  signOff: string;
  length: "short" | "medium" | "long";
  emoji: boolean;
  complaintStyle: string;
  alwaysSay: string[];
  neverSay: string[];
  defaultLanguage: string;
  /** The owner changed the generated card; a refresh must not overwrite it. */
  editedByOwner: boolean;
  generatedAt: Date;
}

/** Per-location Google review auto-reply settings. */
export interface LocationReviewAutoReply {
  enabled?: boolean;
  activatedAt?: Date;
  voiceCard?: ReviewVoiceCard;
}

export interface ILocation {
  // Core Identifiers
  readonly restaurantId: number;
  readonly guid: string;
  readonly slug: string;
  /**
   * Slugs this location USED to have. `GET /locations/slug/:slug` resolves one
   * to this location and the storefront redirects to the current `slug`, so a
   * printed or emailed link survives a rename (`locationSlug.ts`). Server-written
   * only: the rules refuse a client write (`slugAliasesUntouched`).
   */
  readonly slugAliases?: readonly string[];
  readonly tenantId: number;
  readonly tenantGuid?: string; // Added for easier hook usage
  // Platform-controlled payment configuration. Tenants do not write any
  // of these fields — only the admin app sets them during merchant
  // onboarding (Firestore rules enforce this). Co-located with the
  // restaurant identity (rather than in a subcollection) so a single
  // doc read returns "which provider does this restaurant use AND
  // what credentials" — see `validateProviderCredentialConsistency`
  // in `@order-up/schemas`.
  readonly providerRouting?: IPaymentRoutingSettings; // Provider routing — which gateway processes transactions
  /**
   * Whether this restaurant's payments move REAL money.
   *
   * A separate axis from {@link providerRouting}: routing picks the provider,
   * this picks whether the money is real. In `sandbox` the restaurant keeps its
   * routed provider but transacts against that provider's CERT gateway using
   * PLATFORM credentials, so nothing is ever charged.
   *
   * **Absent means `live`** — every restaurant that predates this field is
   * genuinely live, and an absent-means-sandbox reading would divert the whole
   * existing estate to cert at once. See {@link DEFAULT_PAYMENT_MODE}.
   */
  readonly paymentMode?: PaymentMode;
  readonly guestCardCapture?: IGuestCardCapture;
  readonly cardPaymentsVersion?: number;
  /**
   * Which TRX merchant account (MID) each channel — reader-read vs everything
   * else — is processed under. Absent channel ⇒ `primary` (the location's
   * `trxCredentials`). STAFF-written only. See `payment/merchantAccounts.ts`.
   */
  readonly merchantAccountRouting?: IMerchantAccountRouting;
  /**
   * Every additional merchant account ever added (append-only). Their secrets
   * live in the vault (`payment_credentials/{loc}/merchant_accounts/{id}`);
   * this list is what tells a follow-on and the batch close that the location
   * has more than one account to look at.
   */
  readonly trxMerchantAccountIds?: readonly string[];
  readonly trxCredentials?: TrxCredentials; // TRX gateway credentials — WRITE INPUT only; the secret persists in the sealed vault, never on the doc
  readonly trxCredentialsPublic?: TrxCredentialsPublic; // Persisted NON-SECRET projection of the TRX credentials (identifiers + hasPassword)
  readonly approvedRails?: MerchantApprovedRails; // Per-rail underwriting/activation record — capability, NOT routing (see MerchantApprovedRails)

  // Basic Business Information
  readonly name: string;
  readonly displayName?: string; // Customer-facing name shown on website and apps
  readonly description?: string; // Business description (synced to Google Business Profile)
  readonly concept: string; // Primary category/concept (legacy, use categories for multi)
  readonly businessTypeGuid?: string; // FK to platform business_types collection
  readonly categories?: readonly RestaurantCategory[]; // Multiple business categories (Google Business)
  readonly openingDate?: Date; // When the business opened

  // Location & Contact
  readonly address: LocationAddress;
  readonly contact: LocationContact;
  readonly receiptPresentation?: LocationReceiptPresentation;
  readonly coordinates?: GeoCoordinates; // Lat/long for maps

  // Operating Hours
  /**
   * Hours-mode discriminator. Optional for backward-compat — legacy docs
   * with no `hoursMode` field are treated as `'fixed'` (the only mode that
   * existed before this field was introduced). The Restaurant class
   * constructor defaults to `'fixed'` when absent.
   */
  readonly hoursMode?: LocationHoursMode;
  readonly operatingHours: Record<string, DayHours>;
  readonly specialHours?: readonly SpecialHours[]; // Holidays, events, temporary changes
  /**
   * When online and marketplace orders are taken, if different from the
   * business hours in `operatingHours`. Absent or `null` = same as business
   * hours. Read it through `onlineOrderingHoursOf`, never directly.
   */
  readonly onlineOrderingHours?: Record<string, DayHours> | null;

  // Online Ordering & Delivery — managed from the Takeout & Delivery surface.
  /** Whether the restaurant accepts online (first-party) orders. */
  readonly onlineOrderingEnabled?: boolean;
  /**
   * Time-bound first-party online-ordering pause. When set to a FUTURE instant,
   * new online (first-party) intake is refused until it elapses; a past/absent
   * value is a no-op. The auto-expiring analogue of the marketplace
   * `setStoreStatus(until)` — written by the New-Order Awareness escalation
   * ladder's terminal-rung auto-pause (never clobbers the permanent
   * `onlineOrderingEnabled` operator toggle). Only NEW intake is paused; orders
   * already accepted stand.
   */
  readonly onlineOrderingPausedUntil?: Date;
  /** Whether delivery is offered for online orders. */
  readonly deliveryEnabled?: boolean;
  /** Accept online orders right up to closing time (vs. an earlier cutoff). */
  readonly acceptOrdersUntilClosing?: boolean;
  /** Per-marketplace ordering toggle, keyed by provider (doordash/ubereats/grubhub). */
  readonly thirdPartyOrdering?: Record<string, "on" | "off">;
  /** Default single-use items included with takeout/delivery orders. */
  readonly packagingPreferences?: {
    readonly includeNapkins?: boolean;
    readonly includeUtensils?: boolean;
    readonly includeCondiments?: boolean;
    readonly customItems?: readonly string[];
    readonly notes?: string;
  };

  // Service & Dining Options
  readonly serviceOptions?: LocationServiceOptions;
  readonly attributes?: LocationAttributes;

  // Status & Settings
  readonly status: LocationStatus;
  readonly settings: LocationSettings;

  // Platform Integrations
  readonly platformSync?: LocationPlatformSync;
  readonly googleBusinessProfile?: GoogleBusinessProfile; // Synced Google Business Profile data (cache)

  // Website Configuration
  readonly websiteConfig?: LocationWebsiteConfig;
  /**
   * Owner's work-in-progress DRAFT of the website config. When present, the
   * management builder edits THIS (not `websiteConfig`) and the owner-preview
   * path renders it; the public renderer and the publish gate always read the
   * live `websiteConfig` ONLY. "Publish changes" copies this onto
   * `websiteConfig` and clears it; "Discard draft" deletes it. ABSENT is the
   * default and behaves exactly as before this field existed. A public API
   * response never carries it — it is stripped by `sanitizeRestaurantForPublic`
   * (blocklist) so unpublished content never leaves the server.
   */
  readonly websiteConfigDraft?: LocationWebsiteConfig;

  // POS Configuration
  readonly posConfig?: IPosConfig;
  /** Saved menu-import naming house style; absent until the operator saves one. */
  readonly menuImportHouseStyle?: HouseStyleProfile;

  // Feature Flags
  readonly features?: LocationFeatures;
  readonly reviewAutoReply?: LocationReviewAutoReply;

  // Timestamps
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface ILocationData {
  // Core Identifiers
  restaurantId: number;
  guid: string;
  slug: string;
  tenantId: number;
  tenantGuid?: string;
  providerRouting?: IPaymentRoutingSettings;
  paymentMode?: PaymentMode;
  guestCardCapture?: IGuestCardCapture;
  cardPaymentsVersion?: number;
  merchantAccountRouting?: IMerchantAccountRouting;
  trxMerchantAccountIds?: string[];
  trxCredentials?: TrxCredentials;
  /**
   * Pre-derived non-secret projection. `fromJSON` supplies this so the client
   * model can show WHICH TRX credentials are configured without ever carrying
   * the password. Server-side callers pass full `trxCredentials` instead and
   * the constructor derives it.
   */
  trxCredentialsPublic?: TrxCredentialsPublic;
  /**
   * Per-rail capability record. Normalized fail-closed by the constructor via
   * {@link toMerchantApprovedRails} — an absent value stays absent (no rail is
   * granted by omission).
   */
  approvedRails?: MerchantApprovedRails;

  // Basic Business Information
  name: string;
  displayName?: string;
  description?: string;
  concept?: string;
  businessTypeGuid?: string;
  categories?: RestaurantCategory[];
  openingDate?: Date;

  // Location & Contact
  address: LocationAddress;
  contact: LocationContact;
  receiptPresentation?: LocationReceiptPresentation;
  coordinates?: GeoCoordinates;

  // Operating Hours
  hoursMode?: LocationHoursMode;
  operatingHours?: Record<string, DayHours>;
  specialHours?: SpecialHours[];
  onlineOrderingHours?: Record<string, DayHours> | null;

  // Online Ordering & Delivery
  onlineOrderingEnabled?: boolean;
  onlineOrderingPausedUntil?: Date;
  deliveryEnabled?: boolean;
  acceptOrdersUntilClosing?: boolean;
  thirdPartyOrdering?: Record<string, "on" | "off">;
  packagingPreferences?: ILocation["packagingPreferences"];

  // Service & Dining Options
  serviceOptions?: Partial<LocationServiceOptions>;
  attributes?: Partial<LocationAttributes>;

  // Status & Settings
  status?: LocationStatus;
  settings?: Partial<LocationSettings>;

  // Platform Integrations
  platformSync?: Partial<LocationPlatformSync>;
  googleBusinessProfile?: GoogleBusinessProfile;

  // Website Configuration
  websiteConfig?: Partial<LocationWebsiteConfig>;
  websiteConfigDraft?: Partial<LocationWebsiteConfig>;

  // POS Configuration
  posConfig?: Partial<IPosConfig>;
  menuImportHouseStyle?: HouseStyleProfile;

  // Feature Flags
  features?: Partial<LocationFeatures>;
  reviewAutoReply?: LocationReviewAutoReply;

  // Timestamps
  createdAt?: Date;
  updatedAt?: Date;
}

export class Location implements ILocation {
  // Core Identifiers
  readonly restaurantId: number;
  readonly guid: string;
  readonly slug: string;
  readonly tenantId: number;
  readonly tenantGuid?: string;
  readonly providerRouting?: IPaymentRoutingSettings;
  readonly paymentMode?: PaymentMode;
  readonly guestCardCapture?: IGuestCardCapture;
  readonly cardPaymentsVersion?: number;
  /**
   * Which TRX merchant account (MID) each channel — reader-read vs everything
   * else — is processed under. Absent channel ⇒ `primary` (the location's
   * `trxCredentials`). STAFF-written only. See `payment/merchantAccounts.ts`.
   */
  readonly merchantAccountRouting?: IMerchantAccountRouting;
  /**
   * Every additional merchant account ever added (append-only). Their secrets
   * live in the vault (`payment_credentials/{loc}/merchant_accounts/{id}`);
   * this list is what tells a follow-on and the batch close that the location
   * has more than one account to look at.
   */
  readonly trxMerchantAccountIds?: readonly string[];
  readonly trxCredentials?: TrxCredentials;
  /**
   * Non-secret view of {@link trxCredentials} — see {@link TrxCredentialsPublic}.
   *
   * It IS declared on {@link ILocation}, it IS persisted on the restaurant doc
   * (the sealed-vault contract moved the secret off the doc, so this projection
   * is the only credential presence signal left on it), it IS mirrored in the
   * `.strict()` `RestaurantSchema`, and `toJSON` DOES emit it — omitting it
   * would destroy the identifiers + `hasPassword` flag on every model
   * round-trip. `trxCredentials` itself is the field that never round-trips:
   * the constructor accepts it as write input, and `toJSON`/`fromJSON`
   * deliberately drop it so the secret cannot ride on a model shared by
   * checkout, ordering and POS.
   */
  readonly trxCredentialsPublic?: TrxCredentialsPublic;
  /**
   * Per-rail underwriting/activation record — see {@link MerchantApprovedRails}.
   * Persisted, non-secret, platform-written. Read it with
   * {@link isRailApproved}; absent means NO rail is approved.
   */
  readonly approvedRails?: MerchantApprovedRails;

  // Basic Business Information
  readonly name: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly concept: string;
  readonly businessTypeGuid?: string;
  readonly categories?: readonly RestaurantCategory[];
  readonly openingDate?: Date;

  // Location & Contact
  readonly address: LocationAddress;
  readonly contact: LocationContact;
  readonly receiptPresentation?: LocationReceiptPresentation;
  readonly coordinates?: GeoCoordinates;

  // Operating Hours
  readonly hoursMode: LocationHoursMode;
  readonly operatingHours: Record<string, DayHours>;
  readonly specialHours?: readonly SpecialHours[];
  readonly onlineOrderingHours?: Record<string, DayHours>;

  // Online Ordering & Delivery — the Takeout & Delivery surface's fields.
  // These are declared on ILocation but were absent from the class, the
  // constructor and both serializers, so every hydrated `Restaurant` DROPPED
  // them: `SelectionContext` builds instances through
  // `FirestoreRepository.fromJSON`, so the management page's hydration guard
  // (`typeof r.onlineOrderingEnabled === "boolean"`) never fired and its
  // Ordering switch rendered its `useState(true)` default regardless of the
  // stored value — then wrote that default back on the next save.
  readonly onlineOrderingEnabled?: boolean;
  readonly onlineOrderingPausedUntil?: Date;
  readonly deliveryEnabled?: boolean;
  readonly acceptOrdersUntilClosing?: boolean;
  readonly thirdPartyOrdering?: Record<string, "on" | "off">;
  // Same defect, same fix: absent from the class, so the Takeout & Delivery
  // page's Packaging section rendered its defaults after every reload and
  // wrote them back on the next save.
  readonly packagingPreferences?: ILocation["packagingPreferences"];

  // Service & Dining Options
  readonly serviceOptions?: LocationServiceOptions;
  readonly attributes?: LocationAttributes;

  // Status & Settings
  readonly status: LocationStatus;
  readonly settings: LocationSettings;

  // Platform Integrations
  readonly platformSync?: LocationPlatformSync;
  readonly googleBusinessProfile?: GoogleBusinessProfile;

  // Website Configuration
  readonly websiteConfig?: LocationWebsiteConfig;
  /** Owner's work-in-progress website draft (see ILocation docblock). */
  readonly websiteConfigDraft?: LocationWebsiteConfig;

  // POS Configuration
  readonly posConfig?: IPosConfig;
  /** Saved menu-import naming house style; absent until the operator saves one. */
  readonly menuImportHouseStyle?: HouseStyleProfile;

  // Feature Flags
  readonly features?: LocationFeatures;
  readonly reviewAutoReply?: LocationReviewAutoReply;

  // Timestamps
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(data: ILocationData) {
    // Core Identifiers
    this.restaurantId = data.restaurantId;
    this.guid = data.guid;
    this.slug = data.slug;
    this.tenantId = data.tenantId;
    this.tenantGuid = data.tenantGuid;
    this.providerRouting = data.providerRouting;
    this.paymentMode = data.paymentMode;
    this.guestCardCapture = data.guestCardCapture;
    this.cardPaymentsVersion = data.cardPaymentsVersion;
    this.merchantAccountRouting = data.merchantAccountRouting;
    this.trxMerchantAccountIds = data.trxMerchantAccountIds;
    this.trxCredentials = data.trxCredentials
      ? {
          clientId: data.trxCredentials.clientId,
          sourceId: data.trxCredentials.sourceId,
          password: data.trxCredentials.password,
          ...(data.trxCredentials.payPageGuid !== undefined && {
            payPageGuid: data.trxCredentials.payPageGuid,
          }),
          ...(data.trxCredentials.key !== undefined && {
            key: data.trxCredentials.key,
          }),
          ...(data.trxCredentials.iv !== undefined && {
            iv: data.trxCredentials.iv,
          }),
        }
      : undefined;
    // Prefer an explicitly-supplied projection (fromJSON derives it from the
    // raw doc WITHOUT letting the secret onto the client model); otherwise
    // derive it from the full credentials the server-side caller passed.
    this.trxCredentialsPublic =
      data.trxCredentialsPublic ?? toTrxCredentialsPublic(data.trxCredentials);
    // Fail-closed normalization: only a literal `true` grants a rail, and an
    // absent map stays absent (never materialized as "both approved").
    this.approvedRails = toMerchantApprovedRails(data.approvedRails);

    // Basic Business Information
    this.name = data.name;
    this.displayName = data.displayName;
    this.description = data.description;
    this.concept = data.concept ?? "Restaurant";
    this.businessTypeGuid = data.businessTypeGuid;
    this.categories = data.categories ? [...data.categories] : undefined;
    this.openingDate = data.openingDate;

    // Location & Contact
    this.address = data.address;
    // `contact` is typed as required, but minimal/bootstrap-provisioned docs
    // (e.g. the OrderUp HQ restaurant) may omit it. Guard the dereference so a
    // missing `contact` yields an empty contact object instead of throwing and
    // failing the whole restaurants query (see RestaurantService warnings).
    this.contact = {
      phone: data.contact?.phone ?? "",
      email: data.contact?.email ?? "",
      website: data.contact?.website,
      additionalPhones: data.contact?.additionalPhones
        ? [...data.contact.additionalPhones]
        : undefined,
    };
    // Carried through the class (rather than left to `ILocation` alone) so a
    // model round-trip cannot silently erase it: `toJSON` returns `ILocation`
    // and TypeScript does not flag a MISSING optional key, which is exactly how
    // a configured footer would vanish on the next save through the model.
    this.receiptPresentation = data.receiptPresentation
      ? {
          ...(data.receiptPresentation.footerLines
            ? { footerLines: [...data.receiptPresentation.footerLines] }
            : {}),
          ...(data.receiptPresentation.tipPercents
            ? { tipPercents: [...data.receiptPresentation.tipPercents] }
            : {}),
        }
      : undefined;
    this.coordinates = data.coordinates;

    // Operating Hours. `hoursMode` defaults to `'fixed'` for legacy docs
    // written before this field existed — they had a 7-day schedule and
    // no other mode was possible. New docs always carry the field.
    this.hoursMode = data.hoursMode ?? 'fixed';
    // Anti-corruption layer: canonicalize day keys to full lowercase names on
    // read so every consumer can look up by canonical key with no fallback.
    this.operatingHours =
      normalizeOperatingHoursKeys(data.operatingHours) ??
      this.getDefaultOperatingHours();
    this.specialHours = data.specialHours ? [...data.specialHours] : undefined;
    // `null` (cleared) and absent both mean "same as business hours".
    this.onlineOrderingHours = normalizeOperatingHoursKeys(data.onlineOrderingHours ?? undefined);

    // Online Ordering & Delivery. Preserved as-is — `undefined` is meaningful
    // (unset = accepting, per `restaurantService.isAcceptingOrders`), so these
    // must NOT be defaulted here: coercing absent to `false` would refuse
    // orders for every restaurant that never touched the toggle.
    this.onlineOrderingEnabled = data.onlineOrderingEnabled;
    this.onlineOrderingPausedUntil = data.onlineOrderingPausedUntil;
    this.deliveryEnabled = data.deliveryEnabled;
    this.acceptOrdersUntilClosing = data.acceptOrdersUntilClosing;
    this.thirdPartyOrdering = data.thirdPartyOrdering
      ? { ...data.thirdPartyOrdering }
      : undefined;
    this.packagingPreferences = data.packagingPreferences
      ? {
          ...data.packagingPreferences,
          ...(data.packagingPreferences.customItems
            ? { customItems: [...data.packagingPreferences.customItems] }
            : {}),
        }
      : undefined;

    // Service & Dining Options
    this.serviceOptions = data.serviceOptions
      ? this.buildServiceOptions(data.serviceOptions)
      : undefined;
    this.attributes = data.attributes
      ? this.buildAttributes(data.attributes)
      : undefined;

    // Status & Settings
    this.status = data.status ?? "active";
    this.settings = {
      currency: data.settings?.currency ?? "USD",
      allowTips: data.settings?.allowTips ?? true,
      cardTipCollectionMode: data.settings?.cardTipCollectionMode,
      tipAuthBuffer: data.settings?.tipAuthBuffer,
      orderAcceptanceMode: data.settings?.orderAcceptanceMode,
      guestWifi: data.settings?.guestWifi,
      guestOrdering: data.settings?.guestOrdering,
      weekStartDay: data.settings?.weekStartDay ?? "monday",
      timezone: data.settings?.timezone,
      clockFormat: data.settings?.clockFormat,
      dateFormat: data.settings?.dateFormat,
      defaultLanguage: data.settings?.defaultLanguage,
      showSeconds: data.settings?.showSeconds,
      takeoutGuestCount: data.settings?.takeoutGuestCount ?? 0,
      // Undefined = no owner; deliberately not defaulted.
      marketplaceOrderEmployeeId: data.settings?.marketplaceOrderEmployeeId,
      batchOutTime: data.settings?.batchOutTime,
      autoClockOutTime: data.settings?.autoClockOutTime,
      lastAutoClockOutDate: data.settings?.lastAutoClockOutDate,
      lastAutoBatchCloseDate: data.settings?.lastAutoBatchCloseDate,
      lastAutoDrawerCloseDate: data.settings?.lastAutoDrawerCloseDate,
      alerts: data.settings?.alerts,
      orderAlertRouting: data.settings?.orderAlertRouting,
      // Undefined stays undefined (remote approval off). Without this line the
      // block vanished on every client read and `toJSON` wrote it back stripped.
      remoteApproval: data.settings?.remoteApproval,
      // Same trap as remoteApproval: a key missing here is dropped on every read (#3543).
      shiftAlerts: data.settings?.shiftAlerts,
      // Same trap: undefined stays undefined (off); a key missing here is dropped on every read.
      missedPunchAlerts: data.settings?.missedPunchAlerts,
      feesAndCharges: data.settings?.feesAndCharges,
      offlinePayments: data.settings?.offlinePayments,
      scheduling: data.settings?.scheduling,
      seatCount: data.settings?.seatCount,
      enableSeatNumbering: data.settings?.enableSeatNumbering ?? true,
      // Undefined = off. Deliberately NOT defaulted, so existing restaurants
      // stay opt-out until an admin enables capture.
      requireTimeClockPhoto: data.settings?.requireTimeClockPhoto,
      // Undefined = allowed (spec §14b). Deliberately NOT defaulted: the key is
      // server-written, and a missing copy here drops it on every read (#3543).
      allowStaffAppClockIn: data.settings?.allowStaffAppClockIn,
      // Undefined = off, like the requireTimeClockPhoto line.
      promptTakeoutCustomerInfo: data.settings?.promptTakeoutCustomerInfo,
      // Undefined = skip allowed; not defaulted, for the same reason.
      takeoutIntakeAllowSkip: data.settings?.takeoutIntakeAllowSkip,
      courierProvider: data.settings?.courierProvider,
      customerCourierSmsEnabled: data.settings?.customerCourierSmsEnabled,
      estimatedPrepTimeMinutes: data.settings?.estimatedPrepTimeMinutes,
      orderAutopilot: data.settings?.orderAutopilot,
      kitchenPacing: data.settings?.kitchenPacing,
      // Courier dropoff policy — undefined stays undefined (absent on the
      // wire); see the RestaurantSettings JSDoc for the contactless/signature
      // exclusion.
      courierContactlessDropoff: data.settings?.courierContactlessDropoff,
      courierDropoffSignatureRequired:
        data.settings?.courierDropoffSignatureRequired,
      courierIdVerificationRequired: data.settings?.courierIdVerificationRequired,
      courierDropoffPhotoRequired: data.settings?.courierDropoffPhotoRequired,
      cashTipGovernance: data.settings?.cashTipGovernance,
      // Server-written sparse block; undefined stays undefined.
      cashTips: data.settings?.cashTips,
      // Server-written sparse block; undefined stays undefined.
      cardHolds: data.settings?.cardHolds,
      // LEGACY server-written block (retired 2026-10-06; gates nothing).
      // Undefined stays undefined.
      onlineServiceFeeEnrollment: data.settings?.onlineServiceFeeEnrollment,
      // Undefined stays undefined — see RestaurantSettings.printing.
      printing: data.settings?.printing,
      giftCards: data.settings?.giftCards,
      booking: data.settings?.booking,
      // Normalised onto the current contract: the retired tier-pricing keys
      // (`tierPrices`, `mixerPrice`) are dropped, the block stays sparse.
      barSpiritLists: barSpiritListsBlockFromJSON(data.settings?.barSpiritLists),
      barWineList: barWineListBlockFromJSON(data.settings?.barWineList),
    };

    // Platform Integrations
    this.platformSync = data.platformSync
      ? this.buildPlatformSync(data.platformSync)
      : undefined;
    this.googleBusinessProfile = data.googleBusinessProfile;

    // Website Configuration
    this.websiteConfig = data.websiteConfig
      ? this.buildWebsiteConfig(data.websiteConfig)
      : undefined;
    // The DRAFT is normalized through the SAME builder as the live config, so a
    // draft hydrates with identical defaults. Built only when a draft actually
    // exists on the doc — so a restaurant with no draft is unchanged (the field
    // stays `undefined`, the builder edits the live config, preview renders it).
    this.websiteConfigDraft = data.websiteConfigDraft
      ? this.buildWebsiteConfig(data.websiteConfigDraft)
      : undefined;

    // POS Configuration — merge partial config over concept-based defaults
    this.posConfig = buildPosConfig(data.posConfig, this.concept);
    this.menuImportHouseStyle = data.menuImportHouseStyle;

    // Feature Flags
    // Every flag is filled from RESTAURANT_FEATURE_DEFAULTS, which is typed
    // exhaustively — naming keys here is what dropped four flags in a row.
    this.features = hydrateLocationFeatures(data.features);
    this.reviewAutoReply = data.reviewAutoReply;

    // Timestamps
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  // Build service options with defaults
  private buildServiceOptions(
    options: Partial<LocationServiceOptions>,
  ): LocationServiceOptions {
    return {
      dineIn: options.dineIn ?? true,
      takeout: options.takeout ?? true,
      delivery: options.delivery ?? false,
      curbsidePickup: options.curbsidePickup ?? false,
      noContactDelivery: options.noContactDelivery ?? false,
      driveThrough: options.driveThrough,
      outdoorDining: options.outdoorDining,
    };
  }

  // Build attributes (all optional, no defaults)
  private buildAttributes(
    attrs: Partial<LocationAttributes>,
  ): LocationAttributes {
    return { ...attrs } as LocationAttributes;
  }

  // Helper methods
  isActive(): boolean {
    return this.status === "active";
  }

  isOpen(day: string, time: string): boolean {
    // By-appointment restaurants accept orders any time; the merchant
    // dictates availability via appointments rather than via fixed hours.
    // Without this guard, a by-appointment restaurant with the seeded
    // (closed-on-this-day or default) `operatingHours` would be reported
    // as "never open" — the bug this enum fixes.
    if (this.hoursMode === 'by_appointment') return true;

    // operatingHours keys are canonicalized to full lowercase day names on read
    // (see the constructor / fromJSON), so a single canonical lookup is correct.
    // For a timezone-correct evaluation prefer `isRestaurantOpenAt`.
    const dayHours = this.operatingHours[canonicalDayKey(day)];
    if (!dayHours || dayHours.closed) return false;

    return time >= dayHours.open && time <= dayHours.close;
  }

  getDisplayName(): string {
    return this.displayName || this.name || "Unknown Restaurant";
  }

  getFullAddress(): string {
    const { street, street2, city, state, zipCode, country } = this.address;
    const line1 = street2?.trim() ? `${street}, ${street2}` : street;
    return `${line1}, ${city}, ${state} ${zipCode}, ${country}`;
  }

  /**
   * @deprecated This method is simplified and should not be used for real calculations.
   * Tax rates live in the `tax_rates` subcollection.
   * Use the payment service for accurate total calculations.
   */
  calculateTotal(
    subtotal: number,
    includeTip?: boolean,
    tipAmount?: number,
  ): number {
    let total = subtotal;

    // NOTE: Tax and service charge calculations removed - tax rates live in the
    // `tax_rates` subcollection as they vary by item type, location, and order type

    // Add tip if specified
    if (includeTip && tipAmount && this.settings.allowTips) {
      total += tipAmount;
    }

    return Math.round(total * 100) / 100; // Round to 2 decimal places
  }

  private buildPlatformSync(
    data: Partial<LocationPlatformSync>,
  ): LocationPlatformSync {
    // Import serializeTimestamps utility from @order-up/core at runtime to avoid circular dependency
    // The conversion happens here to ensure platform data always has ISO strings
    const serializeTimestamps = (obj: any): any => {
      if (!obj) return obj;

      // Firestore Timestamp with toDate() method
      if (obj && typeof obj === "object" && typeof obj.toDate === "function") {
        return obj.toDate().toISOString();
      }

      // Plain timestamp object with seconds/nanoseconds
      if (
        obj &&
        typeof obj === "object" &&
        typeof obj.seconds === "number" &&
        typeof obj.nanoseconds === "number"
      ) {
        return new Date(
          obj.seconds * 1000 + obj.nanoseconds / 1000000,
        ).toISOString();
      }

      // Date object
      if (obj instanceof Date) {
        return obj.toISOString();
      }

      // Array
      if (Array.isArray(obj)) {
        return obj.map(serializeTimestamps);
      }

      // Object
      if (typeof obj === "object") {
        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
          result[key] = serializeTimestamps(value);
        }
        return result;
      }

      return obj;
    };

    return {
      enabled: data.enabled ?? false,
      autoSync: data.autoSync ?? false,
      syncFrequency: data.syncFrequency ?? "daily",
      platforms: {
        googleMyBusiness: data.platforms?.googleMyBusiness
          ? serializeTimestamps(data.platforms.googleMyBusiness)
          : undefined,
        yelp: data.platforms?.yelp
          ? serializeTimestamps(data.platforms.yelp)
          : undefined,
        facebook: data.platforms?.facebook
          ? serializeTimestamps(data.platforms.facebook)
          : undefined,
        instagram: data.platforms?.instagram
          ? serializeTimestamps(data.platforms.instagram)
          : undefined,
        appleMaps: data.platforms?.appleMaps
          ? serializeTimestamps(data.platforms.appleMaps)
          : undefined,
        doordash: data.platforms?.doordash
          ? serializeTimestamps(data.platforms.doordash)
          : undefined,
        ubereats: data.platforms?.ubereats
          ? serializeTimestamps(data.platforms.ubereats)
          : undefined,
      },
    };
  }

  private buildWebsiteConfig(
    data: Partial<LocationWebsiteConfig>,
  ): LocationWebsiteConfig {
    return {
      enabled: data.enabled ?? false,
      parentMenuGroupId: data.parentMenuGroupId,
      layoutTemplate: data.layoutTemplate,
      templateStyle: data.templateStyle,
      templateKey: data.templateKey,
      lastEditedBy: data.lastEditedBy,
      lastEditedAt: data.lastEditedAt,
      branding: {
        logo: data.branding?.logo,
        logoMode: data.branding?.logoMode,
        heroDisplay: data.branding?.heroDisplay,
        icon: data.branding?.icon,
        heroImage: data.branding?.heroImage,
        heroImageOpacity: data.branding?.heroImageOpacity,
        heroWelcomeText: data.branding?.heroWelcomeText,
        heroWelcomeSize: data.branding?.heroWelcomeSize,
        bannerImage: data.branding?.bannerImage,
        backgroundImage: data.branding?.backgroundImage,
        menuPreviewImage: data.branding?.menuPreviewImage,
        storyImage: data.branding?.storyImage,
        featureImage: data.branding?.featureImage,
        supplementaryImages: data.branding?.supplementaryImages
          ? [...data.branding.supplementaryImages]
          : undefined,
        heroSlides: data.branding?.heroSlides
          ? [...data.branding.heroSlides]
          : undefined,
        spotlight: data.branding?.spotlight
          ? { ...data.branding.spotlight }
          : undefined,
        useBrandColors: data.branding?.useBrandColors,
        fonts: data.branding?.fonts
          ? {
              heading: data.branding.fonts.heading,
              body: data.branding.fonts.body,
            }
          : undefined,
        colors: {
          primary: data.branding?.colors?.primary ?? "#dc2626",
          secondary: data.branding?.colors?.secondary ?? "#1f2937",
          accent: data.branding?.colors?.accent ?? "#f59e0b",
          primaryOpacity: data.branding?.colors?.primaryOpacity ?? 100,
          secondaryOpacity: data.branding?.colors?.secondaryOpacity ?? 100,
          accentOpacity: data.branding?.colors?.accentOpacity ?? 100,
          buttonColor: data.branding?.colors?.buttonColor,
          buttonOpacity: data.branding?.colors?.buttonOpacity,
          // Pass-through (no default): absent = legacy pre-flag behavior.
          customized: data.branding?.colors?.customized,
        },
      },
      seo: {
        title:
          data.seo?.title ??
          `${this.displayName || this.name} - Restaurant`.slice(0, 60),
        description:
          data.seo?.description ??
          `Delicious ${this.concept.toLowerCase()} cuisine at ${this.displayName || this.name}. Visit us for an unforgettable dining experience.`.slice(
            0,
            160,
          ),
        keywords: data.seo?.keywords ?? [
          "restaurant",
          "food",
          "dining",
          this.concept.toLowerCase(),
        ],
        ogImage: data.seo?.ogImage,
        priceRange: data.seo?.priceRange,
        priceMetrics: data.seo?.priceMetrics,
        // Mapped explicitly: this builder drops anything it does not name, and
        // a silently dropped opt-out re-publishes pages the operator turned off.
        generatedPages: data.seo?.generatedPages,
        verification: data.seo?.verification,
      },
      content: {
        aboutText:
          data.content?.aboutText ??
          `Welcome to ${this.displayName || this.name}! We're passionate about serving delicious ${this.concept.toLowerCase()} cuisine made with fresh, quality ingredients.`,
        specialOffers: data.content?.specialOffers ?? [],
        announcement: data.content?.announcement,
      },
      contentBlocks: data.contentBlocks ?? undefined, // Configurable content blocks
      ctaSections: data.ctaSections ?? undefined, // Configurable CTA sections
      // Owner-created standalone pages. Defaults to [] (not undefined) so the
      // renderer/nav/sitemap can always iterate without a guard.
      customPages: data.customPages ? [...data.customPages] : [],
      processSteps: data.processSteps ?? undefined, // Configurable process steps
      processStepsTitle: data.processStepsTitle ?? undefined,
      serviceFeatures: data.serviceFeatures ?? undefined, // Configurable service features
      serviceFeaturesTitle: data.serviceFeaturesTitle ?? undefined,
      serviceFeaturesSubtitle: data.serviceFeaturesSubtitle ?? undefined,
      galleryEnabled: data.galleryEnabled ?? undefined,
      // Pass-through (no default): absent = off for most families, on for
      // multi-location (legacy) — see the interface docblock.
      newsletterEnabled: data.newsletterEnabled ?? undefined,
      galleryImages: data.galleryImages ? [...data.galleryImages] : undefined,
      galleryPhotos: data.galleryPhotos ? [...data.galleryPhotos] : undefined,
      menuSections: data.menuSections ? [...data.menuSections] : undefined,
      contactForm: data.contactForm
        ? {
            enabled: data.contactForm.enabled ?? false,
            heading: data.contactForm.heading,
            subheading: data.contactForm.subheading,
            successMessage: data.contactForm.successMessage,
          }
        : undefined,
      featuredItems: data.featuredItems
        ? {
            menuItemGuids: [...data.featuredItems.menuItemGuids],
            source: data.featuredItems.source,
            blurbs: data.featuredItems.blurbs
              ? { ...data.featuredItems.blurbs }
              : undefined,
            generatedAt: data.featuredItems.generatedAt,
          }
        : undefined,
      social: {
        facebook: data.social?.facebook,
        instagram: data.social?.instagram,
        twitter: data.social?.twitter,
        tiktok: data.social?.tiktok,
      },
      ordering: {
        enabled: data.ordering?.enabled ?? false,
        provider: data.ordering?.provider ?? "internal",
        url: data.ordering?.url,
        buttonText: data.ordering?.buttonText ?? "Order Now",
        buttonColor: data.ordering?.buttonColor ?? "#dc2626",
        // Enumerated field-by-field, NOT passed through, for the same reason
        // every other branch of this builder is: hydration is the boundary
        // that normalizes a stored doc onto the CURRENT contract. `config`
        // used to be a whole-object pass-through, so keys retired from the
        // schema (`maxAdvanceOrderDays`, `enableScheduledOrders`, removed
        // 2026-07-10) rode a legacy doc through the model and back into the
        // admin modal's read-modify-write payload, where the `.strict()`
        // RestaurantOrderingConfigSchema rejected them — a restaurant
        // provisioned before that date could not be saved at all. Enumerating
        // also fills fields a doc predates (all schema-defaulted), so the
        // built object actually satisfies the all-required interface.
        config: {
          minLeadTimeMinutes: data.ordering?.config?.minLeadTimeMinutes ?? 15,
          timeSlotIntervalMinutes:
            data.ordering?.config?.timeSlotIntervalMinutes ?? 15,
          closingBufferMinutes:
            data.ordering?.config?.closingBufferMinutes ?? 15,
          enableAsapOrders: data.ordering?.config?.enableAsapOrders ?? true,
          enableDelivery: data.ordering?.config?.enableDelivery ?? false,
          enablePickup: data.ordering?.config?.enablePickup ?? true,
        },
      },
      analytics: data.analytics
        ? {
            ga4MeasurementId: data.analytics.ga4MeasurementId,
            metaPixelId: data.analytics.metaPixelId,
            gtmId: data.analytics.gtmId,
          }
        : undefined,
      instagramFeed: data.instagramFeed
        ? {
            enabled: data.instagramFeed.enabled ?? undefined,
            mode: data.instagramFeed.mode ?? undefined,
            displayCount: data.instagramFeed.displayCount ?? undefined,
            manualPosts: data.instagramFeed.manualPosts
              ? [...data.instagramFeed.manualPosts]
              : undefined,
            headingEyebrow: data.instagramFeed.headingEyebrow ?? undefined,
          }
        : undefined,
    };
  }

  private getDefaultOperatingHours(): Record<string, DayHours> {
    const defaultHours: DayHours = {
      open: "09:00",
      close: "22:00",
      closed: false,
    };

    return {
      monday: { ...defaultHours },
      tuesday: { ...defaultHours },
      wednesday: { ...defaultHours },
      thursday: { ...defaultHours },
      friday: { ...defaultHours, close: "23:00" },
      saturday: { ...defaultHours, close: "23:00" },
      sunday: { ...defaultHours, open: "10:00", close: "21:00" },
    };
  }

  // Serialization methods
  toJSON(): ILocation {
    return {
      // Core Identifiers
      restaurantId: this.restaurantId,
      guid: this.guid,
      slug: this.slug,
      tenantId: this.tenantId,
      tenantGuid: this.tenantGuid,

      // Basic Business Information
      name: this.name,
      displayName: this.displayName,
      description: this.description,
      concept: this.concept,
      businessTypeGuid: this.businessTypeGuid,
      categories: this.categories,
      openingDate: this.openingDate,

      // Location & Contact
      address: this.address,
      contact: this.contact,
      receiptPresentation: this.receiptPresentation,
      coordinates: this.coordinates,

      // Operating Hours
      hoursMode: this.hoursMode,
      operatingHours: this.operatingHours,
      specialHours: this.specialHours,
      onlineOrderingHours: this.onlineOrderingHours,

      // Online Ordering & Delivery
      onlineOrderingEnabled: this.onlineOrderingEnabled,
      onlineOrderingPausedUntil: this.onlineOrderingPausedUntil,
      deliveryEnabled: this.deliveryEnabled,
      acceptOrdersUntilClosing: this.acceptOrdersUntilClosing,
      thirdPartyOrdering: this.thirdPartyOrdering,
      packagingPreferences: this.packagingPreferences,

      // Service & Dining Options
      serviceOptions: this.serviceOptions,
      attributes: this.attributes,

      // Status & Settings
      status: this.status,
      settings: this.settings,

      // Platform Integrations
      platformSync: this.platformSync,
      googleBusinessProfile: this.googleBusinessProfile,

      // Payment routing (processor selection). Emitted so a model round-trip
      // (toJSON → fromJSON) preserves it — see fromJSON for why dropping it
      // mis-routed checkout.
      //
      // `trxCredentials` is deliberately NOT emitted (secrets — they live in
      // the sealed vault, not on the doc). `trxCredentialsPublic` IS emitted:
      // it is the PERSISTED non-secret projection (declared on ILocation and
      // in RestaurantSchema), so a round-trip preserves the identifiers +
      // hasPassword presence flag the admin UI and validators rely on.
      //
      // `approvedRails` is emitted on the same terms: a persisted, non-secret,
      // schema-declared capability record. Spread CONDITIONALLY — an
      // unapproved restaurant must round-trip with the key ABSENT, because
      // materializing `{ card: false, ach: false }` would turn "not yet
      // underwritten" into a written assertion on the doc.
      ...(this.trxCredentialsPublic !== undefined && {
        trxCredentialsPublic: this.trxCredentialsPublic,
      }),
      ...(this.approvedRails !== undefined && {
        approvedRails: this.approvedRails,
      }),
      providerRouting: this.providerRouting,
      paymentMode: this.paymentMode,
      guestCardCapture: this.guestCardCapture,
      cardPaymentsVersion: this.cardPaymentsVersion,
      merchantAccountRouting: this.merchantAccountRouting,
      trxMerchantAccountIds: this.trxMerchantAccountIds,

      // Website Configuration
      websiteConfig: this.websiteConfig,
      websiteConfigDraft: this.websiteConfigDraft,

      // POS Configuration
      posConfig: this.posConfig,
      menuImportHouseStyle: this.menuImportHouseStyle,

      // Feature Flags
      features: this.features,
      reviewAutoReply: this.reviewAutoReply,

      // Timestamps
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromJSON(data: Record<string, any>): Location {
    if (!data.restaurantId || typeof data.restaurantId !== "number")
      throw new Error(
        "Restaurant.fromJSON: restaurantId is missing or invalid.",
      );
    if (!data.guid || typeof data.guid !== "string")
      throw new Error("Restaurant.fromJSON: guid is missing or invalid.");
    if (!data.tenantId || typeof data.tenantId !== "number")
      throw new Error("Restaurant.fromJSON: tenantId is missing or invalid.");
    if (!data.name || typeof data.name !== "string")
      throw new Error("Restaurant.fromJSON: name is missing or invalid.");

    // Helper function to convert Firestore timestamps to Date objects
    const convertTimestamp = (timestamp: any): Date | undefined => {
      if (!timestamp) return undefined;
      // Check if it's a Firestore Timestamp object
      if (timestamp && typeof timestamp.toDate === "function") {
        return timestamp.toDate();
      }
      // Check if it's already a Date object
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Try to parse as string/number
      return new Date(timestamp);
    };

    return new Location({
      // Core Identifiers
      restaurantId: data.restaurantId,
      guid: data.guid,
      slug: data.slug as string,
      tenantId: data.tenantId,
      tenantGuid: data.tenantGuid as string | undefined,

      // Basic Business Information
      name: data.name,
      displayName: data.displayName as string | undefined,
      description: data.description as string | undefined,
      concept: data.concept as string | undefined,
      businessTypeGuid: data.businessTypeGuid as string | undefined,
      categories: data.categories as RestaurantCategory[] | undefined,
      openingDate: convertTimestamp(data.openingDate),

      // Location & Contact
      address: data.address as LocationAddress,
      contact: data.contact as LocationContact,
      receiptPresentation: data.receiptPresentation as
        | LocationReceiptPresentation
        | undefined,
      coordinates: data.coordinates as GeoCoordinates | undefined,

      // Operating Hours
      hoursMode: data.hoursMode as LocationHoursMode | undefined,
      operatingHours: normalizeOperatingHoursKeys(
        data.operatingHours as Record<string, DayHours> | undefined,
      ),
      specialHours: data.specialHours as SpecialHours[] | undefined,
      onlineOrderingHours: normalizeOperatingHoursKeys(
        (data.onlineOrderingHours ?? undefined) as Record<string, DayHours> | undefined,
      ),

      // Online Ordering & Delivery
      onlineOrderingEnabled: data.onlineOrderingEnabled as boolean | undefined,
      onlineOrderingPausedUntil: convertTimestamp(
        data.onlineOrderingPausedUntil,
      ),
      deliveryEnabled: data.deliveryEnabled as boolean | undefined,
      acceptOrdersUntilClosing: data.acceptOrdersUntilClosing as
        | boolean
        | undefined,
      thirdPartyOrdering: data.thirdPartyOrdering as
        | Record<string, "on" | "off">
        | undefined,
      packagingPreferences:
        data.packagingPreferences as ILocation["packagingPreferences"],

      // Service & Dining Options
      serviceOptions: data.serviceOptions as
        | Partial<LocationServiceOptions>
        | undefined,
      attributes: data.attributes as Partial<LocationAttributes> | undefined,

      // Status & Settings
      status: data.status as LocationStatus | undefined,
      settings: data.settings as Partial<LocationSettings> | undefined,

      // Platform Integrations
      platformSync: data.platformSync as
        | Partial<LocationPlatformSync>
        | undefined,
      googleBusinessProfile: readGoogleBusinessProfile(
        data.googleBusinessProfile,
        `restaurants/${String(data.guid ?? data.restaurantId ?? "?")}/googleBusinessProfile`,
      ),

      // Payment routing — which gateway processes transactions. MUST be
      // deserialized here: the checkout reads
      // `restaurant.providerRouting.defaultProvider` to pick the payment UI,
      // and omitting it from fromJSON silently dropped it on read, so the
      // checkout fell back to another processor's UI even for TRX-configured
      // restaurants.
      // NOTE: `trxCredentials` (password/key/iv) is intentionally NOT mapped
      // here — those secrets must not ride along on the client-facing model;
      // the TRX session is minted server-side.
      //
      // But omitting the WHOLE payment config was its own bug: with no
      // projection of `trxCredentials` the admin app re-opened a configured
      // restaurant to EMPTY payment fields — the settings were in Firestore
      // the whole time, destroyed on the way back out. Derive the non-secret
      // half of the TRX credentials so privileged UI can display and edit
      // them without ever receiving the password. (A legacy `merchantId` —
      // a retired processor's identifier — may still sit on old documents;
      // it is deliberately NOT mapped.)
      // Prefer the PERSISTED projection (contract-phase docs carry it and no
      // longer carry `trxCredentials` at all); fall back to deriving it from a
      // legacy doc's raw credential map during the migration window.
      trxCredentialsPublic:
        (data.trxCredentialsPublic as TrxCredentialsPublic | undefined) ??
        toTrxCredentialsPublic(
          data.trxCredentials as Partial<TrxCredentials> | undefined,
        ),
      // Per-rail capability. Passed through the fail-closed normalizer rather
      // than cast: a raw doc is untrusted input, and a truthy-but-not-`true`
      // value must not read as an approved rail.
      approvedRails: toMerchantApprovedRails(data.approvedRails),
      providerRouting: data.providerRouting as IPaymentRoutingSettings | undefined,
      // Carried explicitly: `fromJSON` names every field it keeps, so an
      // omission here would silently drop a restaurant OUT of sandbox mode on
      // any round-trip and start charging real cards.
      paymentMode: data.paymentMode as PaymentMode | undefined,
      guestCardCapture: data.guestCardCapture as IGuestCardCapture | undefined,
      cardPaymentsVersion: typeof data.cardPaymentsVersion === 'number' ? data.cardPaymentsVersion : undefined,
      merchantAccountRouting: data.merchantAccountRouting as IMerchantAccountRouting | undefined,
      trxMerchantAccountIds: Array.isArray(data.trxMerchantAccountIds)
        ? (data.trxMerchantAccountIds as unknown[]).filter((v): v is string => typeof v === 'string')
        : undefined,

      // Website Configuration
      websiteConfig: data.websiteConfig as
        | Partial<LocationWebsiteConfig>
        | undefined,
      websiteConfigDraft: data.websiteConfigDraft as
        | Partial<LocationWebsiteConfig>
        | undefined,

      // POS Configuration
      posConfig: data.posConfig as Partial<IPosConfig> | undefined,
      menuImportHouseStyle: data.menuImportHouseStyle as
        | HouseStyleProfile
        | undefined,

      // Feature Flags
      features: data.features as Partial<LocationFeatures> | undefined,
      reviewAutoReply: hydrateReviewAutoReply(data.reviewAutoReply),

      // Timestamps
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    });
  }
}

/** @deprecated use ILocation (restaurant → location rename) */
export type IRestaurant = ILocation; // restaurant-ok: spec-mandated deprecated alias (Phase 7 deletes)
/** @deprecated use Location (restaurant → location rename) */
export const Restaurant = Location; // restaurant-ok: spec-mandated deprecated alias (Phase 7 deletes)
/** @deprecated use Location (restaurant → location rename) */
export type Restaurant = Location; // restaurant-ok: spec-mandated deprecated alias (Phase 7 deletes)

/** The two doc ids that address one location's Firestore subtree. */
export interface LocationScope {
  readonly tenantDocId: string;
  readonly locationDocId: string;
}
